import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  useMediaQuery,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Icon } from "../../icons";
import { COLORS, TYPOGRAPHY, FONT_FAMILY } from "../../constants/color";
import axios from "axios";
import { useAuth } from "../../context/authContext";
import { url } from "../../constants/url";
import AppToast from "../../components/AppToast";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, addDays, parseISO, isMonday } from "date-fns";
import PhotoUploadWithDrawing from "../../components/siteCheck/PhotoUploadWithDrawing";
import useFormPersistence from "../../hooks/useFormPersistence";

// Custom Rich Text Editor Component
const RichTextEditor = ({ value, onChange, placeholder, height = "161px" }) => {
  const [isFocused, setIsFocused] = useState(false);
  const editorRef = React.useRef(null);

  const handleTextChange = () => {
    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      onChange(content);
    }
  };

  const handleFormat = (command, value = null) => {
    document.execCommand(command, false, value);
    handleTextChange();
    editorRef.current.focus();
  };

  const handleAlignment = (alignment) => {
    const alignmentMap = {
      'left': 'justifyLeft',
      'center': 'justifyCenter',
      'right': 'justifyRight',
      'justify': 'justifyFull'
    };
    document.execCommand(alignmentMap[alignment]);
    handleTextChange();
    editorRef.current.focus();
  };

  const handleList = (listType) => {
    const command = listType === 'unordered' ? 'insertUnorderedList' : 'insertOrderedList';
    document.execCommand(command);
    handleTextChange();
    editorRef.current.focus();
  };

  const handleClearFormat = () => {
    // Select all text first
    document.execCommand('selectAll');
    // Remove all formatting
    document.execCommand('removeFormat');
    // Clear any remaining styles
    document.execCommand('unlink');
    document.execCommand('formatBlock', false, 'p');
    handleTextChange();
    editorRef.current.focus();
  };

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => {
    setIsFocused(false);
    handleTextChange();
  };

  // Set initial content
  React.useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  return (
    <Box>
      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          gap: 1,
          mb: 1,
          p: 1,
          backgroundColor: "#f5f5f5",
          borderRadius: "8px 8px 0 0",
          border: "1px solid #e0e0e0",
          borderBottom: "none",
          flexWrap: "wrap",
        }}
      >
        {/* Text Formatting */}
        <Box display="flex" gap={0.5}>
          <IconButton
            size="small"
            onClick={() => handleFormat('bold')}
            sx={{
              width: 32,
              height: 32,
              backgroundColor: '#fff',
              border: '1px solid #ddd',
              '&:hover': { backgroundColor: '#f0f0f0' }
            }}
            title="Bold"
          >
            <strong>B</strong>
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleFormat('italic')}
            sx={{
              width: 32,
              height: 32,
              backgroundColor: '#fff',
              border: '1px solid #ddd',
              fontStyle: 'italic',
              '&:hover': { backgroundColor: '#f0f0f0' }
            }}
            title="Italic"
          >
            I
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleFormat('underline')}
            sx={{
              width: 32,
              height: 32,
              backgroundColor: '#fff',
              border: '1px solid #ddd',
              textDecoration: 'underline',
              '&:hover': { backgroundColor: '#f0f0f0' }
            }}
            title="Underline"
          >
            U
          </IconButton>
        </Box>

        {/* Alignment */}
        <Box display="flex" gap={0.5}>
          <IconButton
            size="small"
            onClick={() => handleAlignment('left')}
            sx={{
              width: 32,
              height: 32,
              backgroundColor: '#fff',
              border: '1px solid #ddd',
              '&:hover': { backgroundColor: '#f0f0f0' }
            }}
            title="Align Left"
          >
            <Icon name="alignLeft" size={14} color={COLORS.TEXT} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleAlignment('center')}
            sx={{
              width: 32,
              height: 32,
              backgroundColor: '#fff',
              border: '1px solid #ddd',
              '&:hover': { backgroundColor: '#f0f0f0' }
            }}
            title="Align Center"
          >
            <Icon name="alignCenter" size={14} color={COLORS.TEXT} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleAlignment('right')}
            sx={{
              width: 32,
              height: 32,
              backgroundColor: '#fff',
              border: '1px solid #ddd',
              '&:hover': { backgroundColor: '#f0f0f0' }
            }}
            title="Align Right"
          >
            <Icon name="alignRight" size={14} color={COLORS.TEXT} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleAlignment('justify')}
            sx={{
              width: 32,
              height: 32,
              backgroundColor: '#fff',
              border: '1px solid #ddd',
              '&:hover': { backgroundColor: '#f0f0f0' }
            }}
            title="Justify"
          >
            <Icon name="alignJustify" size={14} color={COLORS.TEXT} />
          </IconButton>
        </Box>

        {/* Lists */}
        <Box display="flex" gap={0.5}>
          <IconButton
            size="small"
            onClick={() => handleList('unordered')}
            sx={{
              width: 32,
              height: 32,
              backgroundColor: '#fff',
              border: '1px solid #ddd',
              '&:hover': { backgroundColor: '#f0f0f0' }
            }}
            title="Bulleted List"
          >
            <Icon name="listUnordered" size={14} color={COLORS.TEXT} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleList('ordered')}
            sx={{
              width: 32,
              height: 32,
              backgroundColor: '#fff',
              border: '1px solid #ddd',
              '&:hover': { backgroundColor: '#f0f0f0' }
            }}
            title="Numbered List"
          >
            <Icon name="listOrdered" size={14} color={COLORS.TEXT} />
          </IconButton>
        </Box>

        {/* Clear Formatting */}
        <Button
          size="small"
          onClick={handleClearFormat}
          sx={{
            height: 32,
            fontSize: 11,
            backgroundColor: '#fff',
            border: '1px solid #ddd',
            color: COLORS.PRIMARY,
            '&:hover': { backgroundColor: '#f8f8f8' }
          }}
        >
          Clear Format
        </Button>
      </Box>

      {/* Editor Area */}
      <Box
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleTextChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        sx={{
          border: `1px solid ${isFocused ? COLORS.PRIMARY : COLORS.LIGHT_SECONDARY}`,
          borderRadius: "0 0 8px 8px",
          minHeight: height,
          padding: "12px",
          fontFamily: FONT_FAMILY,
          fontSize: "14px",
          color: COLORS.PRIMARY, // Changed to PRIMARY color
          outline: "none",
          overflowY: "auto",
          backgroundColor: "#fff",
          "&:empty:before": {
            content: `"${placeholder || 'Start typing here...'}"`,
            color: COLORS.LIGHT_SECONDARY,
          },
          "&:focus": {
            borderColor: COLORS.PRIMARY,
          },
          // Ensure proper text styling
          "& p": {
            margin: "0 0 8px 0",
            color: COLORS.PRIMARY,
          },
          "& ul, & ol": {
            margin: "0 0 8px 0",
            paddingLeft: "20px",
            color: COLORS.PRIMARY,
          },
          "& li": {
            color: COLORS.PRIMARY,
          },
        }}
      />
    </Box>
  );
};

// ================= FLEX HELPER COMPONENTS =================
const FlexRow = ({ children, header }) => (
  <Box
    display="flex"
    alignItems="stretch"
    sx={{
      py: header ? 1.2 : 1,
      fontWeight: header ? 600 : 400,
    }}
  >
    {children}
  </Box>
);

const FlexCell = ({ children, flex, w, sx = {}, borderBottom = "none", ...props }) => (
  <Box
    flex={flex}
    width={w}
    px={1}
    sx={{
      ...TYPOGRAPHY.BODY_LARGE,
      borderBottom: borderBottom,
      wordWrap: "break-word",
      whiteSpace: "normal",
      overflowWrap: "break-word",
      lineHeight: 1.4,
      ...sx
    }}
    {...props}
  >
    {children}
  </Box>
);

const FlexRowInner = ({ children, header }) => (
  <Box
    display="flex"
    flex={1}
    alignItems="stretch"
    sx={{
      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
    }}
  >
    {children}
  </Box>
);

const FlexEnd = ({ children, mt }) => (
  <Box display="flex" justifyContent="flex-end" mt={mt}>
    {children}
  </Box>
);

export default function EditWMR() {
  const { projectId, wmrId } = useParams();
  const { token, userData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Debug: Log the URL params
  console.log("EditWMR: URL params - projectId:", projectId, "wmrId:", wmrId);

  // Get week data from navigation state
  const {
    weekStartDate: initialWeekStartDate,
    weekEndDate: initialWeekEndDate,
    projectName: propProjectName,
    cardData
  } = location.state || {};

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [project, setProject] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");

  // Week date state - Initialize from props
  const [weekStartDate, setWeekStartDate] = useState(
    initialWeekStartDate ? new Date(initialWeekStartDate) : null
  );
  const [weekEndDate, setWeekEndDate] = useState(
    initialWeekEndDate ? new Date(initialWeekEndDate) : null
  );

  // Toast state
  const [toast, setToast] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
  });

  // WMR data state
  const [wmrData, setWmrData] = useState({
    workExecuted: {
      rccWork: "",
      civilWorks: "",
      masonryFinishing: "",
      mepWorks: "",
      externalDevelopment: ""
    },
    workInProgress: {
      mergedProgress: []
    },
    manpowerDeployment: {
      laborTypes: [
        { type: "Engineer", data: [0, 0, 0, 0, 0, 0] },
        { type: "Supervisors", data: [0, 0, 0, 0, 0, 0] },
        { type: "Unskilled Labour", data: [0, 0, 0, 0, 0, 0] },
        { type: "Carpenter", data: [0, 0, 0, 0, 0, 0] }
      ],
      dates: []
    },
    machineryEquipment: [
      { equipment: "Excavator", quantity: "", hours: "", utilization: "-" },
      { equipment: "JCB", quantity: "", hours: "", utilization: "-" },
      { equipment: "Concrete Mixer", quantity: "", hours: "", utilization: "-" },
      { equipment: "Tower Crane", quantity: "", hours: "", utilization: "-" },
      { equipment: "Mobile Crane", quantity: "", hours: "", utilization: "-" },
      { equipment: "Concrete Pumping", quantity: "", hours: "", utilization: "-" },
      { equipment: "Vibrator", quantity: "", hours: "", utilization: "-" },
      { equipment: "Compactor", quantity: "", hours: "", utilization: "-" },
      { equipment: "Bar Bending Machine", quantity: "", hours: "", utilization: "-" },
      { equipment: "Bar Cutting Machine", quantity: "", hours: "", utilization: "-" },
      { equipment: "Welding Machine", quantity: "", hours: "", utilization: "-" },
      { equipment: "Generator / DG Set", quantity: "", hours: "", utilization: "-" }
    ],
    materialStatus: [
      { description: "Cement (OPC / PPC)", received: "", consumed: "", balance: "" },
      { description: "Steel / TMT Bars", received: "", consumed: "", balance: "" },
      { description: "Coarse Aggregate (20mm)", received: "", consumed: "", balance: "" },
      { description: "Coarse Aggregate (10mm)", received: "", consumed: "", balance: "" },
      { description: "Fine Aggregate / Sand", received: "", consumed: "", balance: "" },
      { description: "Bricks", received: "", consumed: "", balance: "" },
      { description: "Ready Mix Concrete", received: "", consumed: "", balance: "" },
      { description: "Concrete Blocks", received: "", consumed: "", balance: "" },
      { description: "Plywood / Shuttering", received: "", consumed: "", balance: "" },
      { description: "Binding Wire", received: "", consumed: "", balance: "" },
      { description: "Nails", received: "", consumed: "", balance: "" },
      { description: "MS Channels / Angles", received: "", consumed: "", balance: "" }
    ],
    qualityControl: [
      { inspection: "What Inspection we Carried out", test: "", observation: "Observations in Text Format" },
      { inspection: "Inspection 1", test: "", observation: "Attached file title from Report" },
      { inspection: "Inspection seconds", test: "", observation: "" }
    ],
    safetyHSE: {
      safetyCompliance: "",
      toolboxTalks: "",
      accidentsNearMisses: ""
    },
    issuesConstraints: {
      technical: "",
      material: "",
      manpower: "",
      designApproval: ""
    },
    instructionsDecisions: [],
    reports: [],
    decisionDiscussion: "",
    planNextWeek: {
      targetActivities: "",
      resourcePlanning: ""
    },
    photos: []
  });

  // Form states for dynamic sections
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [showLocationForm, setShowLocationForm] = useState(false);
  const [showMachineEntry, setShowMachineEntry] = useState(false);
  const [showMaterialEntry, setShowMaterialEntry] = useState(false);
  const [showQualityEntry, setShowQualityEntry] = useState(false);
  const [openReports, setOpenReports] = useState(true);

  // Edit state for tracking which entry is being edited
  const [editingEntry, setEditingEntry] = useState({
    section: null, // 'activity', 'location', 'machine', 'material', 'quality', 'plannedActual'
    index: null, // For simple arrays
    dayIndex: null, // For planned vs actual section (which day)
    entryIndex: null, // For planned vs actual section (which entry in the day)
    data: null // The entry data being edited
  });

  // Planned vs Actual section state
  const [openDay, setOpenDay] = useState(null);
  
  const plannedActualPersistenceKey = `wmr-edit-planned-actual-${projectId}-${openDay ?? 'no-day'}-${editingEntry?.section === 'plannedActual' ? editingEntry.data?._id || editingEntry.entryIndex : 'new'}`;
  const [newEntry, setNewEntry, clearNewEntryPersistence] = useFormPersistence(plannedActualPersistenceKey, {
    contractor: "",
    activity: "",
    status: "OPEN",
    variance: "",
    expectedCompletionDate: null
  });

  const [newProgressEntry, setNewProgressEntry] = useState({
    activity: "",
    location: "",
    status: "",
    remark: ""
  });
  const [newMachine, setNewMachine] = useState({
    equipment: "",
    quantity: "",
    hours: "",
    utilization: ""
  });
  const [newMaterial, setNewMaterial] = useState({
    description: "",
    received: "",
    consumed: "",
    balance: ""
  });
  const [newQuality, setNewQuality] = useState({
    inspection: "",
    test: "",
    observation: "",
    status: "OPEN"
  });
  const [newVisitor, setNewVisitor] = useState({
    date: new Date(),
    visitorName: "",
    designation: "",
    otherDesignation: "",
    instruction: "",
    remark: ""
  });
  const [showVisitorEntry, setShowVisitorEntry] = useState(false);
  const [editingVisitor, setEditingVisitor] = useState(null);
  const [showOtherConsultantInput, setShowOtherConsultantInput] = useState(false);
  const [uploadedReports, setUploadedReports] = useState([]);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [showPhotoUploadDialog, setShowPhotoUploadDialog] = useState(false);
  const [saving, setSaving] = useState(false);

  // Track original files to identify removed ones
  const [originalReportFiles, setOriginalReportFiles] = useState([]);
  const [originalPhotos, setOriginalPhotos] = useState([]);
  const [downloading, setDownloading] = useState(false);

  // Project Layout Image state
  const [projectLayoutImage, setProjectLayoutImage] = useState(null);
  const [projectLayoutImagePreview, setProjectLayoutImagePreview] = useState(null);
  const [originalProjectLayoutImage, setOriginalProjectLayoutImage] = useState(null);

  // Week days data for Planned vs Actual section
  const [weekDaysData, setWeekDaysData] = useState([]);

  // Add a ref to track the current week being processed
  const currentWeekRef = useRef(null);

  // Format date functions
  const formatDateForWeekHeader = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return format(d, "dd/MM/yy");
  };

  const formatDateForWeekColumn = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const day = d.getDate();
    const suffix = getDaySuffix(day);
    const month = d.toLocaleString('default', { month: 'short' });
    const year = d.getFullYear().toString().slice(-2);
    const dayName = d.toLocaleString('default', { weekday: 'long' });

    return `${dayName} ${day}${suffix} ${month} '${year}`;
  };

  const getDaySuffix = (day) => {
    if (day >= 11 && day <= 13) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  // Generate week days data for Planned vs Actual section
  const generateWeekDaysData = (startDate, endDate) => {
    if (!startDate || !endDate) return [];

    const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const weekDataArray = [];

    const currentDate = new Date(startDate);

    for (let i = 0; i < 6; i++) {
      const date = new Date(currentDate);
      date.setDate(currentDate.getDate() + i);

      // Store date in both Date object and string format for easier comparison
      weekDataArray.push({
        day: days[i],
        date: date,
        dateStr: format(date, "yyyy-MM-dd"), // Store as string for easy comparison
        formattedDate: formatDateForWeekColumn(date),
        entries: []
      });
    }

    return weekDataArray;
  };

  // Auto-calculate balance for new material entry
  useEffect(() => {
    const received = parseFloat(newMaterial.received) || 0;
    const consumed = parseFloat(newMaterial.consumed) || 0;
    setNewMaterial(prev => ({
      ...prev,
      balance: (received - consumed).toString()
    }));
  }, [newMaterial.received, newMaterial.consumed]);

  // Fetch project and WMR data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const config = {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        };

        // Fetch project details
        const projectResponse = await axios.get(
          `${url}/api/inhouse-projects/fetch-by/${projectId}`,
          config
        );

        if (projectResponse.data.project) {
          setProject(projectResponse.data.project);

          // Fetch companies for PMC dropdown
          const companiesResponse = await axios.get(
            `${url}/api/company/fetch`,
            config
          );

          if (companiesResponse.data.data) {
            setCompanies(companiesResponse.data.data);
            // Select first company by default
            if (companiesResponse.data.data.length > 0) {
              setSelectedCompany(companiesResponse.data.data[0]._id);
            }
          }

          // If week dates are provided from props, initialize week data
          if (initialWeekStartDate && initialWeekEndDate) {
            const startDate = new Date(initialWeekStartDate);
            const endDate = new Date(initialWeekEndDate);
            setWeekStartDate(startDate);
            setWeekEndDate(endDate);
            initializeWeekData(startDate, endDate);

            // Fetch WMR data for the week (if wmrId is provided, fetch specific WMR)
            if (wmrId) {
              await fetchWMRById();
            } else {
              // Otherwise fetch WMR for the week
              setTimeout(() => {
                fetchWMRForWeek(startDate, endDate);
              }, 500);
            }
          } else {
            setLoading(false);
          }
        } else {
          setError("Project not found");
          setLoading(false);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load project data. Please try again.");
        setLoading(false);
      }
    };

    if (projectId && token) {
      fetchData();
    }
  }, [projectId, token, initialWeekStartDate, initialWeekEndDate, wmrId]);

  // Debug: Monitor weekDaysData changes
  useEffect(() => {
    console.log("EditWMR: weekDaysData changed:", weekDaysData);
  }, [weekDaysData]);

  // Store the WMR data to populate when weekDaysData is ready
  const [wmrDataToPopulate, setWmrDataToPopulate] = useState(null);

  // Populate WMR data when weekDaysData is ready and we have WMR data
  useEffect(() => {
    if (weekDaysData.length > 0 && wmrDataToPopulate) {
      console.log("EditWMR: weekDaysData is ready, populating WMR data");
      populateWMRData(wmrDataToPopulate);
      setWmrDataToPopulate(null); // Clear after populating
    }
  }, [weekDaysData, wmrDataToPopulate]);

  // Fetch WMR by ID
  const fetchWMRById = async () => {
    try {
      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      };

      const response = await axios.get(
        `${url}/api/wmr/fetch/${wmrId}`,
        config
      );

      if (response.data.wmr) {
        const wmr = response.data.wmr;

        // Set week dates from the WMR
        if (wmr.weekStartDate && wmr.weekEndDate) {
          // Robust date parsing to avoid timezone shifts
          let startDate, endDate;
          if (typeof wmr.weekStartDate === 'string') {
            const [year, month, day] = wmr.weekStartDate.split('T')[0].split('-').map(Number);
            startDate = new Date(year, month - 1, day);
          } else {
            startDate = new Date(wmr.weekStartDate);
          }

          if (typeof wmr.weekEndDate === 'string') {
            const [year, month, day] = wmr.weekEndDate.split('T')[0].split('-').map(Number);
            endDate = new Date(year, month - 1, day);
          } else {
            endDate = new Date(wmr.weekEndDate);
          }

          setWeekStartDate(startDate);
          setWeekEndDate(endDate);

          // Initialize week data FIRST
          initializeWeekData(startDate, endDate);

          // Store WMR data to populate when weekDaysData is ready
          setWmrDataToPopulate(wmr);
          console.log("EditWMR: Stored WMR data for population after weekDaysData is ready");
        }

        showToast("success", "Success", "WMR data loaded successfully!");
      }
    } catch (err) {
      console.error("Error fetching WMR by ID:", err);
      showToast("error", "Error", "Failed to load WMR data.");
    }
  };

  // Fetch WMR data for the selected week
  const fetchWMRForWeek = async (startDate, endDate) => {
    try {
      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      };

      // Format dates for API
      const weekStartStr = format(startDate, "yyyy-MM-dd");
      const weekEndStr = format(endDate, "yyyy-MM-dd");

      // Fetch WMR for the selected week and project
      const response = await axios.get(
        `${url}/api/wmr/fetch-by-project/${projectId}?weekStartDate=${weekStartStr}&weekEndDate=${weekEndStr}`,
        config
      );

      if (response.data.wmrs && response.data.wmrs.length > 0) {
        const wmr = response.data.wmrs[0]; // Get the first WMR for that week

        // Validate that the returned WMR matches the requested week
        const wmrStartStr = format(parseISO(wmr.weekStartDate), "yyyy-MM-dd");
        const wmrEndStr = format(parseISO(wmr.weekEndDate), "yyyy-MM-dd");

        if (wmrStartStr === weekStartStr && wmrEndStr === weekEndStr) {
          populateWMRData(wmr);
          showToast("info", "WMR Found", "Existing WMR data loaded for this week.");
          return true;
        }
      }
      return false;
    } catch (err) {
      console.error("Error fetching WMR data:", err);
      return false;
    }
  };

  // Initialize week data
  const initializeWeekData = (startDateParam, endDate) => {
    // Generate dates for the week
    const dates = [];
    let startDate;
    if (typeof startDateParam === 'string') {
      const [year, month, day] = startDateParam.split('T')[0].split('-').map(Number);
      startDate = new Date(year, month - 1, day);
    } else {
      startDate = new Date(startDateParam);
    }

    for (let i = 0; i < 6; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      // Format to just date without time for comparison
      dates.push(format(date, "yyyy-MM-dd"));
    }

    // Update manpower deployment dates (formatted differently)
    const displayDates = dates.map(date => format(new Date(date), "dd/MM/yy"));

    setWmrData(prev => ({
      ...prev,
      manpowerDeployment: {
        ...prev.manpowerDeployment,
        dates: displayDates
      }
    }));

    // Initialize week days data for Planned vs Actual section
    const weekDays = generateWeekDaysData(startDate, endDate);
    console.log("EditWMR: WeekDaysData initialized:", weekDays);
    setWeekDaysData(weekDays);

    setLoading(false);
  };

  // Populate form data from existing WMR
  const populateWMRData = (wmr, weekDaysParam = null) => {
    console.log("=== populateWMRData Diagnostic (EditWMR) ===");
    console.log("WMR ManpowerDeployment raw:", JSON.stringify(wmr.manpowerDeployment, null, 2));
    console.log("Project Layout Image data:", wmr.projectLayoutImage);
    console.log("weekDaysParam received:", weekDaysParam);
    console.log("Current weekDaysData state:", weekDaysData);

    // Work Executed During Week
    if (wmr.workExtendedDuringWeek && wmr.workExtendedDuringWeek.length > 0) {
      const workExecuted = {
        rccWork: "",
        civilWorks: "",
        masonryFinishing: "",
        mepWorks: "",
        externalDevelopment: ""
      };

      wmr.workExtendedDuringWeek.forEach(work => {
        if (work.workType === "RCC Works") workExecuted.rccWork = work.description || "";
        else if (work.workType === "Civil Works") workExecuted.civilWorks = work.description || "";
        else if (work.workType === "Masonry / Finishing Works") workExecuted.masonryFinishing = work.description || "";
        else if (work.workType === "MEP Works") workExecuted.mepWorks = work.description || "";
        else if (work.workType === "External Development") workExecuted.externalDevelopment = work.description || "";
      });
      setWmrData(prev => ({ ...prev, workExecuted }));
    }

    // Work In Progress - Handle backend data properly
    if (wmr.workInProgress && wmr.workInProgress.length > 0) {
      console.log("EditWMR: Populating work in progress data");
      const mergedProgress = [];

      // Backend sends workInProgress as a single array with all fields
      // Convert each entry to frontend format
      wmr.workInProgress.forEach(entry => {
        mergedProgress.push({
          activity: entry.activity || "",
          location: entry.location || "",
          status: entry.status || "",
          remark: entry.remarks || "" // Note: backend uses 'remarks' field
        });
      });

      console.log("EditWMR: Processed workInProgress:", mergedProgress);

      setWmrData(prev => ({
        ...prev,
        workInProgress: {
          mergedProgress: mergedProgress
        }
      }));
    }

    // Planned vs Actual Progress
    if (wmr.plannedActualProgress && wmr.plannedActualProgress.length > 0) {
      console.log("EditWMR: Populating planned vs actual data:", wmr.plannedActualProgress);

      // Create a fresh copy of weekDaysData
      const updatedWeekDays = weekDaysData.map(day => ({
        ...day,
        entries: [] // Clear existing entries to avoid duplicates
      }));

      console.log("EditWMR: UpdatedWeekDays before population:", updatedWeekDays);

      wmr.plannedActualProgress.forEach(entry => {
        const entryDateStr = typeof entry.dayDate === 'string' ? entry.dayDate.split('T')[0] :
          format(new Date(entry.dayDate), 'yyyy-MM-dd');
        console.log(`EditWMR: Processing entry for date: ${entryDateStr}`);

        // Find matching day in weekDaysData
        const dayIndex = updatedWeekDays.findIndex(day => {
          const dayDateStr = format(new Date(day.date), 'yyyy-MM-dd');
          return dayDateStr === entryDateStr;
        });

        if (dayIndex !== -1) {
          console.log(`EditWMR: Found match at index ${dayIndex} for date ${entryDateStr}`);

          if (!updatedWeekDays[dayIndex].entries) {
            updatedWeekDays[dayIndex].entries = [];
          }

          updatedWeekDays[dayIndex].entries.push({
            contractor: entry.contractorVendorName || "",
            activity: entry.targetedActivityDetails || "",
            status: entry.status || "OPEN",
            variance: entry.varianceReason || "",
            expectedCompletionDate: entry.expectedCompletionDate ? new Date(entry.expectedCompletionDate) : null
          });

          console.log(`EditWMR: Added entry to day ${dayIndex}:`, updatedWeekDays[dayIndex].entries);
        } else {
          console.log(`EditWMR: No match found for date ${entryDateStr}`);
          console.log("EditWMR: Available dates:", updatedWeekDays.map(d => format(d.date, "yyyy-MM-dd")));
        }
      });

      console.log("EditWMR: Final updatedWeekDays:", updatedWeekDays);
      setWeekDaysData(updatedWeekDays);
    } else {
      console.log("EditWMR: No planned actual progress data found");
    }

    // Manpower Deployment
    if (wmr.manpowerDeployment && wmr.manpowerDeployment.length > 0) {
      // Re-generate the 6 dates of the week for consistent mapping (YYYY-MM-DD)
      // Robust date generation to avoid timezone shifts
      const weekDates = [];
      let startDate;
      if (typeof wmr.weekStartDate === 'string') {
        const [year, month, day] = wmr.weekStartDate.split('T')[0].split('-').map(Number);
        startDate = new Date(year, month - 1, day);
      } else {
        startDate = new Date(wmr.weekStartDate);
      }

      for (let i = 0; i < 6; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        weekDates.push(format(date, "dd/MM/yy"));
      }
      const displayDates = weekDates;
      console.log("Generated weekDates for mapping:", weekDates);

      const laborGroups = {};
      wmr.manpowerDeployment.forEach(item => {
        const laborType = (item.labourType || item.laborType || "").trim();
        if (!laborType) return;

        if (!laborGroups[laborType]) {
          laborGroups[laborType] = new Array(6).fill(0);
        }

        // Ensure dailyCount is a plain object
        let dailyCount = item.dailyCount;
        if (dailyCount && typeof dailyCount === 'object') {
          if (dailyCount.$__parent) {
            dailyCount = Object.fromEntries(dailyCount);
          } else if (typeof dailyCount.toJSON === 'function') {
            dailyCount = dailyCount.toJSON();
          }
        } else {
          dailyCount = {};
        }

        console.log(`EditWMR - Processing labour type: ${laborType}`, dailyCount);

        Object.entries(dailyCount).forEach(([date, count]) => {
          // Robust matching: normalize date string to dd/MM/yy
          const normalizeDate = (dStr) => {
            if (!dStr) return "";
            let s = dStr.toString().trim();

            // Handle ISO format YYYY-MM-DD...
            if (s.includes('-')) {
              const parts = s.split('T')[0].split('-');
              if (parts.length === 3) {
                const [y, m, d] = parts;
                return `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y.slice(-2)}`;
              }
            }

            // Handle DD/MM/YY or DD/MM/YYYY
            if (s.includes('/')) {
              const parts = s.split('/');
              if (parts.length === 3) {
                const d = parts[0].padStart(2, '0');
                const m = parts[1].padStart(2, '0');
                let y = parts[2];
                if (y.length === 4) y = y.slice(-2);
                return `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y.padStart(2, '0')}`;
              }
            }

            try {
              const parsed = new Date(s);
              if (!isNaN(parsed.getTime())) {
                return format(parsed, "dd/MM/yy");
              }
            } catch (e) { }

            return s;
          };

          const normalizedDate = normalizeDate(date);
          let dateIndex = weekDates.indexOf(normalizedDate);

          // Fallback: Digits-only matching
          if (dateIndex === -1) {
            const searchDigits = normalizedDate.replace(/\D/g, '').slice(-6);
            dateIndex = weekDates.findIndex(wd => wd.replace(/\D/g, '').slice(-6) === searchDigits);
          }

          if (dateIndex !== -1) {
            laborGroups[laborType][dateIndex] = (laborGroups[laborType][dateIndex] || 0) + (parseInt(count) || 0);
          } else {
            console.warn(`EditWMR - No match for date ${date} (normalized: ${normalizedDate}) in weekDates`, weekDates);
          }
        });
      });

      const laborTypes = Object.entries(laborGroups).map(([type, data]) => ({
        type,
        data: data.map(v => v || 0) // Ensure no nulls
      }));

      setWmrData(prev => ({
        ...prev,
        manpowerDeployment: {
          laborTypes,
          dates: displayDates
        }
      }));
    } else {
      // Handle empty manpower deployment - show default structure
      const weekDates = [];
      const startDate = new Date(wmr.weekStartDate);
      for (let i = 0; i < 6; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        weekDates.push(format(date, "dd/MM/yy")); // Match database format
      }

      const defaultLaborTypes = [
        { type: "Engineer", data: [0, 0, 0, 0, 0, 0] },
        { type: "Supervisors", data: [0, 0, 0, 0, 0, 0] },
        { type: "Masons", data: [0, 0, 0, 0, 0, 0] },
        { type: "Helpers", data: [0, 0, 0, 0, 0, 0] }
      ];

      setWmrData(prev => ({
        ...prev,
        manpowerDeployment: {
          laborTypes: defaultLaborTypes,
          dates: weekDates
        }
      }));
    }

    // Machinery Equipment
    if (wmr.machineryEquipment && wmr.machineryEquipment.length > 0) {
      setWmrData(prev => ({
        ...prev,
        machineryEquipment: wmr.machineryEquipment.map(m => ({
          equipment: m.typeOfEquipment || "",
          quantity: m.quantity?.toString() || "0",
          hours: m.workingHours?.toString() || "0",
          utilization: m.utilizationStatus || ""
        }))
      }));
    }

    // Material Status
    if (wmr.materialStatus && wmr.materialStatus.length > 0) {
      setWmrData(prev => ({
        ...prev,
        materialStatus: wmr.materialStatus.map(m => ({
          description: m.materialDescription || "",
          received: m.materialReceived?.toString() || "0",
          consumed: m.materialConsumed?.toString() || "0",
          balance: (m.balanceAtSite || m.materialBalance)?.toString() || "0"
        }))
      }));
    }

    // Quality Control
    if (wmr.qualityControlActivities && wmr.qualityControlActivities.length > 0) {
      setWmrData(prev => ({
        ...prev,
        qualityControl: wmr.qualityControlActivities.map(q => ({
          inspection: q.inspectionCarriedOut || "",
          test: q.testConducted || "",
          observation: q.ncrObservation || "",
          status: q.status || "OPEN"
        }))
      }));
    }

    // Safety & HSE
    if (wmr.safetyHSEStatus) {
      setWmrData(prev => ({
        ...prev,
        safetyHSE: {
          safetyCompliance: wmr.safetyHSEStatus.safetyCompliance || "",
          toolboxTalks: wmr.safetyHSEStatus.toolboxTalks || "",
          accidentsNearMisses: wmr.safetyHSEStatus.accidentsNearMisses || ""
        }
      }));
    }

    // Issues Constraints
    if (wmr.issuesConstraints) {
      setWmrData(prev => ({
        ...prev,
        issuesConstraints: {
          technical: wmr.issuesConstraints.technical || "",
          material: wmr.issuesConstraints.material || "",
          manpower: wmr.issuesConstraints.manpower || "",
          designApproval: wmr.issuesConstraints.designApproval || ""
        }
      }));
    }

    // Instructions Decisions
    if (wmr.instructionsDecisions && Array.isArray(wmr.instructionsDecisions)) {
      setWmrData(prev => ({
        ...prev,
        instructionsDecisions: wmr.instructionsDecisions.map(instruction => ({
          date: instruction.date ? new Date(instruction.date) : new Date(),
          visitorName: instruction.visitorName || "",
          designation: instruction.designation || "",
          instruction: instruction.instructions || "",
          remark: instruction.remarks || ""
        }))
      }));
    }

    // Decision Discussion
    if (wmr.decisionRecords && wmr.decisionRecords.length > 0) {
      setWmrData(prev => ({
        ...prev,
        decisionDiscussion: wmr.decisionRecords[0].record || ""
      }));
    }

    // Plan for Next Week
    if (wmr.planNextWeek && wmr.planNextWeek.length > 0) {
      setWmrData(prev => ({
        ...prev,
        planNextWeek: {
          targetActivities: wmr.planNextWeek[0].targetActivity || "",
          resourcePlanning: wmr.planNextWeek[0].resourcePlanning || ""
        }
      }));
    }

    // Report Files
    if (wmr.reportFiles && wmr.reportFiles.length > 0) {
      const reportFiles = wmr.reportFiles.map(file => ({
        name: file.fileName,
        size: 0,
        type: "application/pdf",
        path: file.filePath
      }));
      setUploadedReports(reportFiles);
      setOriginalReportFiles(reportFiles); // Track original files
    }

    // Photos
    if (wmr.photographicProgress && wmr.photographicProgress.length > 0) {
      const photos = wmr.photographicProgress.map(photo => ({
        photoPath: photo.photoPath,
        description: photo.description || "",
        shapes: photo.shapes || [],
        uploadedAt: photo.uploadedAt,
        name: photo.description || "Photo",
        imageUrl: `${url}${photo.photoPath.replace(/\\/g, '/')}`
      }));
      setUploadedPhotos(photos);
      setOriginalPhotos(photos); // Track original photos for removal
    }

    // Project Layout Image
    if (wmr.projectLayoutImage) {
      setOriginalProjectLayoutImage(wmr.projectLayoutImage);
      // Convert backslashes to forward slashes and ensure proper URL format
      const imagePath = wmr.projectLayoutImage.filePath.replace(/\\/g, '/');
      const imageUrl = imagePath.startsWith('/') ? `${url}${imagePath}` : `${url}/${imagePath}`;
      setProjectLayoutImagePreview(imageUrl);
      console.log("EditWMR: Project layout image loaded:", imageUrl);
    } else {
      console.log("EditWMR: No project layout image found in WMR data");
    }
  };

  // Handle download report
  const handleDownloadReport = async () => {
    try {
      setDownloading(true);

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        responseType: 'blob'
      };

      // Use wmrId if available, otherwise fetch the WMR for the week first
      let targetWmrId = wmrId;

      if (!targetWmrId) {
        // Fetch WMR for the week to get the ID
        const weekStartStr = format(weekStartDate, "yyyy-MM-dd");
        const weekEndStr = format(weekEndDate, "yyyy-MM-dd");

        const response = await axios.get(
          `${url}/api/wmr/fetch-by-project/${projectId}?weekStartDate=${weekStartStr}&weekEndDate=${weekEndStr}`,
          config
        );

        if (response.data.wmrs && response.data.wmrs.length > 0) {
          targetWmrId = response.data.wmrs[0]._id;
        } else {
          showToast("error", "Error", "No WMR found to download.");
          setDownloading(false);
          return;
        }
      }

      // Download the report
      const downloadResponse = await axios.get(
        `${url}/api/wmr/download/${targetWmrId}/report`,
        config
      );

      // Create blob and download
      const blob = new Blob([downloadResponse.data], { type: 'application/pdf' });
      const urlBlob = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = urlBlob;

      // Generate filename
      const dateStr = weekStartDate ? format(weekStartDate, 'yyyy-MM-dd') : new Date().toISOString().split('T')[0];
      const projectName = project?.projectName?.replace(/[^a-zA-Z0-9]/g, '_') || 'WMR';
      link.download = `WMR_${projectName}_${dateStr}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(urlBlob);

      showToast("success", "Success", "WMR report downloaded successfully!");

    } catch (err) {
      console.error("Error downloading WMR report:", err);
      const errorMessage = err.response?.data?.message || "Failed to download WMR report. Please try again.";
      showToast("error", "Error", errorMessage);
    } finally {
      setDownloading(false);
    }
  };

  // Handle visitor change
  const handleVisitorChange = (index, field, value) => {
    const updatedVisitors = [...wmrData.instructionsDecisions];
    updatedVisitors[index][field] = value;
    setWmrData(prev => ({
      ...prev,
      instructionsDecisions: updatedVisitors
    }));
  };

  // Handle visitor deletion
  const handleDeleteVisitor = (index) => {
    const updatedVisitors = [...wmrData.instructionsDecisions];
    updatedVisitors.splice(index, 1);
    setWmrData(prev => ({
      ...prev,
      instructionsDecisions: updatedVisitors
    }));
  };

  // Handle edit visitor
  const handleEditVisitor = (index) => {
    const visitor = wmrData.instructionsDecisions[index];
    setEditingVisitor(index);
    setNewVisitor({
      date: visitor.date,
      visitorName: visitor.visitorName,
      designation: visitor.designation,
      otherDesignation: visitor.designation === "Other Consultant" ? visitor.designation : "",
      instruction: visitor.instruction,
      remark: visitor.remark
    });
    setShowOtherConsultantInput(visitor.designation === "Other Consultant");
    setShowVisitorEntry(true);
  };

  // Handle designation change
  const handleDesignationChange = (value) => {
    setNewVisitor({ ...newVisitor, designation: value });
    setShowOtherConsultantInput(value === "Other Consultant");
    if (value !== "Other Consultant") {
      setNewVisitor(prev => ({ ...prev, otherDesignation: "" }));
    }
  };

  // Handle add visitor
  const handleAddVisitor = () => {
    const finalDesignation = newVisitor.designation === "Other Consultant"
      ? newVisitor.otherDesignation
      : newVisitor.designation;

    const visitorData = {
      ...newVisitor,
      designation: finalDesignation
    };

    if (editingVisitor !== null) {
      // Update existing visitor
      const updatedVisitors = [...wmrData.instructionsDecisions];
      updatedVisitors[editingVisitor] = visitorData;
      setWmrData(prev => ({
        ...prev,
        instructionsDecisions: updatedVisitors
      }));
    } else {
      // Add new visitor
      setWmrData(prev => ({
        ...prev,
        instructionsDecisions: [
          ...prev.instructionsDecisions,
          visitorData
        ]
      }));
    }

    // Reset form
    setNewVisitor({
      date: new Date(),
      visitorName: "",
      designation: "",
      otherDesignation: "",
      instruction: "",
      remark: ""
    });
    setShowVisitorEntry(false);
    setEditingVisitor(null);
    setShowOtherConsultantInput(false);
  };

  // ================= HANDLER FUNCTIONS =================

  // Planned vs Actual handlers
  const handleAddPlannedVsActualEntry = (dayIndex) => {
    // Validation: If status is OPEN, variance and expected completion date are required
    if (newEntry.status === "OPEN") {
      if (!newEntry.variance || newEntry.variance.trim() === "") {
        showToast("error", "Validation Error", "Variance/Reason is required when status is Open");
        return;
      }
      if (!newEntry.expectedCompletionDate) {
        showToast("error", "Validation Error", "Expected Completion Date is required when status is Open");
        return;
      }
    }

    const updatedWeekDays = [...weekDaysData];
    if (!updatedWeekDays[dayIndex].entries) {
      updatedWeekDays[dayIndex].entries = [];
    }

    if (editingEntry.section === 'plannedActual' && editingEntry.entryIndex !== null) {
      // Update existing entry
      updatedWeekDays[dayIndex].entries[editingEntry.entryIndex] = { ...newEntry };
      setEditingEntry({
        section: null,
        dayIndex: null,
        entryIndex: null,
        data: null
      });
    } else {
      // Add new entry
      updatedWeekDays[dayIndex].entries.push({ ...newEntry });
    }

    setWeekDaysData(updatedWeekDays);
    if (clearNewEntryPersistence) clearNewEntryPersistence();
    setNewEntry({ contractor: "", activity: "", status: "OPEN", variance: "", expectedCompletionDate: null });
    setOpenDay(null);
  };

  const handleDeletePlannedVsActualEntry = (dayIndex, entryIndex) => {
    const updatedWeekDays = [...weekDaysData];
    updatedWeekDays[dayIndex].entries.splice(entryIndex, 1);
    setWeekDaysData(updatedWeekDays);
  };

  // Manpower Deployment handlers
  const handleManpowerChange = (typeIndex, dayIndex, value) => {
    const updatedLaborTypes = [...wmrData.manpowerDeployment.laborTypes];
    updatedLaborTypes[typeIndex].data[dayIndex] = parseInt(value) || 0;

    setWmrData(prev => ({
      ...prev,
      manpowerDeployment: {
        ...prev.manpowerDeployment,
        laborTypes: updatedLaborTypes
      }
    }));
  };

  const handleAddLaborType = () => {
    setWmrData(prev => ({
      ...prev,
      manpowerDeployment: {
        ...prev.manpowerDeployment,
        laborTypes: [
          ...prev.manpowerDeployment.laborTypes,
          { type: `New Labor ${prev.manpowerDeployment.laborTypes.length + 1}`, data: [0, 0, 0, 0, 0, 0] }
        ]
      }
    }));
  };

  // Machinery Equipment handlers
  const handleMachineChange = (index, field, value) => {
    const updatedMachinery = [...wmrData.machineryEquipment];
    updatedMachinery[index][field] = value;
    setWmrData(prev => ({
      ...prev,
      machineryEquipment: updatedMachinery
    }));
  };

  const handleDeleteMachine = (index) => {
    const updatedMachinery = [...wmrData.machineryEquipment];
    updatedMachinery.splice(index, 1);
    setWmrData(prev => ({
      ...prev,
      machineryEquipment: updatedMachinery
    }));
  };

  // Material Status handlers
  const handleMaterialChange = (index, field, value) => {
    const updatedMaterials = [...wmrData.materialStatus];
    updatedMaterials[index][field] = value;

    // Calculate balance if received or consumed changed
    if (field === 'received' || field === 'consumed') {
      const received = parseFloat(updatedMaterials[index].received) || 0;
      const consumed = parseFloat(updatedMaterials[index].consumed) || 0;
      updatedMaterials[index].balance = (received - consumed).toString();
    }

    setWmrData(prev => ({
      ...prev,
      materialStatus: updatedMaterials
    }));
  };

  const handleDeleteMaterial = (index) => {
    const updatedMaterials = [...wmrData.materialStatus];
    updatedMaterials.splice(index, 1);
    setWmrData(prev => ({
      ...prev,
      materialStatus: updatedMaterials
    }));
  };

  // Quality Control handlers
  const handleQualityChange = (index, field, value) => {
    const updatedQuality = [...wmrData.qualityControl];
    updatedQuality[index][field] = value;
    setWmrData(prev => ({
      ...prev,
      qualityControl: updatedQuality
    }));
  };

  const handleDeleteQuality = (index) => {
    const updatedQuality = [...wmrData.qualityControl];
    updatedQuality.splice(index, 1);
    setWmrData(prev => ({
      ...prev,
      qualityControl: updatedQuality
    }));
  };

  // Add new entry handlers
  const handleAddMachine = () => {
    if (editingEntry.section === 'machine' && editingEntry.index !== null) {
      // Update existing entry
      const updatedMachinery = [...wmrData.machineryEquipment];
      updatedMachinery[editingEntry.index] = { ...newMachine };
      setWmrData(prev => ({
        ...prev,
        machineryEquipment: updatedMachinery
      }));
      setEditingEntry({
        section: null,
        index: null,
        data: null
      });
    } else {
      // Add new entry
      setWmrData(prev => ({
        ...prev,
        machineryEquipment: [
          ...prev.machineryEquipment,
          { ...newMachine }
        ]
      }));
    }
    setNewMachine({ equipment: "", quantity: "", hours: "", utilization: "" });
    setShowMachineEntry(false);
  };

  const handleAddMaterial = () => {
    // Calculate balance before adding
    const received = parseFloat(newMaterial.received) || 0;
    const consumed = parseFloat(newMaterial.consumed) || 0;
    const balance = (received - consumed).toString();

    if (editingEntry.section === 'material' && editingEntry.index !== null) {
      // Update existing entry
      const updatedMaterials = [...wmrData.materialStatus];
      updatedMaterials[editingEntry.index] = { ...newMaterial, balance };
      setWmrData(prev => ({
        ...prev,
        materialStatus: updatedMaterials
      }));
      setEditingEntry({
        section: null,
        index: null,
        data: null
      });
    } else {
      // Add new entry
      setWmrData(prev => ({
        ...prev,
        materialStatus: [
          ...prev.materialStatus,
          { ...newMaterial, balance }
        ]
      }));
    }
    setNewMaterial({ description: "", received: "", consumed: "", balance: "" });
    setShowMaterialEntry(false);
  };

  const handleAddQuality = () => {
    if (editingEntry.section === 'quality' && editingEntry.index !== null) {
      // Update existing entry
      const updatedQuality = [...wmrData.qualityControl];
      updatedQuality[editingEntry.index] = { ...newQuality };
      setWmrData(prev => ({
        ...prev,
        qualityControl: updatedQuality
      }));
      setEditingEntry({
        section: null,
        index: null,
        data: null
      });
    } else {
      // Add new entry
      setWmrData(prev => ({
        ...prev,
        qualityControl: [
          ...prev.qualityControl,
          { ...newQuality }
        ]
      }));
    }
    setNewQuality({ inspection: "", test: "", observation: "", status: "OPEN" });
    setShowQualityEntry(false);
  };

  // Handle merged work in progress entry
  const handleAddProgressEntry = () => {
    if (editingEntry.section === 'progress' && editingEntry.index !== null) {
      // Update existing entry
      const updatedProgress = [...wmrData.workInProgress.mergedProgress];
      updatedProgress[editingEntry.index] = { ...newProgressEntry };
      setWmrData(prev => ({
        ...prev,
        workInProgress: {
          mergedProgress: updatedProgress
        }
      }));
      setEditingEntry({
        section: null,
        index: null,
        data: null
      });
    } else {
      // Add new entry
      setWmrData(prev => ({
        ...prev,
        workInProgress: {
          mergedProgress: [
            ...(prev.workInProgress?.mergedProgress || []),
            { ...newProgressEntry }
          ]
        }
      }));
    }
    setNewProgressEntry({ activity: "", location: "", status: "", remark: "" });
    setShowActivityForm(false);
  };

  const handleDeleteProgressEntry = (index) => {
    const updatedProgress = [...wmrData.workInProgress.mergedProgress];
    updatedProgress.splice(index, 1);
    setWmrData(prev => ({
      ...prev,
      workInProgress: {
        mergedProgress: updatedProgress
      }
    }));
  };

  // Report handlers
  const handleReportUpload = (event) => {
    const files = Array.from(event.target.files);
    setUploadedReports(prev => [...prev, ...files]);
  };

  const handleRemoveReport = (index) => {
    setUploadedReports(prev => prev.filter((_, i) => i !== index));
  };

  // Photo handlers
  const handlePhotoUploadWithDrawing = (photoData) => {
    console.log("EditWMR: Photo upload data received:", {
      hasFile: !!photoData.file,
      title: photoData.title,
      titleLength: photoData.title?.length,
      shapesCount: photoData.shapes?.length || 0
    });

    // Ensure title is never empty
    const finalTitle = photoData.title?.trim() || photoData.file?.name?.split('.')[0] || 'Untitled Photo';

    const newPhoto = {
      file: photoData.file,
      title: finalTitle,
      shapes: photoData.shapes,
      imageUrl: photoData.imageUrl,
      name: photoData.file.name,
      size: photoData.file.size,
      type: photoData.file.type
    };

    console.log("EditWMR: Final photo object created:", {
      title: newPhoto.title,
      fileName: newPhoto.name
    });

    setUploadedPhotos(prev => [...prev, newPhoto]);
    setShowPhotoUploadDialog(false);
    showToast("success", "Photo Added", "Photo with annotations added successfully!");
  };

  const handlePhotoUpload = (event) => {
    const files = Array.from(event.target.files);
    if (uploadedPhotos.length + files.length > 4) {
      showToast("error", "Error", "Maximum 4 photos allowed.");
      return;
    }

    const newPhotos = files.map(file => ({
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      imageUrl: URL.createObjectURL(file),
      title: file.name.split('.')[0],
      shapes: []
    }));

    setUploadedPhotos(prev => [...prev, ...newPhotos]);
  };

  const removePhoto = (index) => {
    setUploadedPhotos(prev => prev.filter((_, i) => i !== index));
  };

  // Handle project layout image upload
  const handleProjectLayoutImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type (image only)
    if (!file.type.startsWith('image/')) {
      showToast("error", "Error", "Only image files are allowed for project layout.");
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      showToast("error", "Error", "File size must be less than 10MB.");
      return;
    }

    setProjectLayoutImage(file);
    setProjectLayoutImagePreview(URL.createObjectURL(file));
    showToast("success", "Success", "Project layout image uploaded successfully!");
  };

  // Remove project layout image
  const removeProjectLayoutImage = () => {
    setProjectLayoutImage(null);
    setProjectLayoutImagePreview(null);
    showToast("info", "Removed", "Project layout image removed.");
  };

  // Download project layout image
  const downloadProjectLayoutImage = async () => {
    if (!originalProjectLayoutImage && !projectLayoutImage) {
      showToast("error", "Error", "No project layout image available for download.");
      return;
    }

    try {
      setDownloading(true);

      // Determine which image to download (new uploaded one or original)
      const imageToDownload = projectLayoutImage || originalProjectLayoutImage;
      let imageUrl;

      if (projectLayoutImagePreview) {
        // Use the preview URL for newly uploaded images
        imageUrl = projectLayoutImagePreview;
      } else if (originalProjectLayoutImage?.filePath) {
        // Construct URL for original images from server
        const imagePath = originalProjectLayoutImage.filePath.replace(/\\/g, '/');
        imageUrl = imagePath.startsWith('/') ? `${url}${imagePath}` : `${url}/${imagePath}`;
      } else {
        throw new Error("No valid image URL found");
      }

      // Create download link
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = imageToDownload?.name || originalProjectLayoutImage?.fileName || 'project-layout.jpg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      showToast("success", "Success", "Project layout image downloaded successfully!");
    } catch (error) {
      console.error("Error downloading project layout image:", error);
      showToast("error", "Error", "Failed to download project layout image.");
    } finally {
      setDownloading(false);
    }
  };

  // Calculate manpower totals and averages
  const calculateManpowerValues = () => {
    const laborTypes = wmrData.manpowerDeployment.laborTypes;
    const totals = {};
    const averages = {};
    const columnTotals = [0, 0, 0, 0, 0, 0];
    let grandTotal = 0;

    laborTypes.forEach(labor => {
      const values = labor.data;
      const total = values.reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
      const average = values.length > 0 ? Math.round(total / values.length) : 0;

      totals[labor.type] = total;
      averages[labor.type] = average;
      grandTotal += total;

      values.forEach((val, index) => {
        columnTotals[index] += (parseFloat(val) || 0);
      });
    });

    const columnAverages = columnTotals.map(total =>
      laborTypes.length > 0 ? Math.round(total / laborTypes.length) : 0
    );

    const grandAverage = laborTypes.length > 0 ? Math.round(grandTotal / (laborTypes.length * 6)) : 0;

    return { totals, averages, columnTotals, columnAverages, grandTotal, grandAverage };
  };

  // Update WMR
  const handleUpdateWMR = async () => {
    try {
      setSaving(true);

      // Validate wmrId
      if (!wmrId) {
        console.error("EditWMR: No wmrId provided for update");
        showToast("error", "Error", "WMR ID is missing. Cannot update.");
        return;
      }

      console.log("EditWMR: Updating WMR with ID:", wmrId);

      const formData = new FormData();

      // Add basic fields
      formData.append('projectId', projectId);
      formData.append('weekStartDate', format(weekStartDate, "yyyy-MM-dd"));
      formData.append('weekEndDate', format(weekEndDate, "yyyy-MM-dd"));
      formData.append('title', `WMR - ${formatDateForWeekHeader(weekStartDate)} to ${formatDateForWeekHeader(weekEndDate)}`);

      // Add work executed sections
      formData.append('workExtendedDuringWeek', JSON.stringify([
        { workType: "RCC Works", description: wmrData.workExecuted.rccWork },
        { workType: "Civil Works", description: wmrData.workExecuted.civilWorks },
        { workType: "Masonry / Finishing Works", description: wmrData.workExecuted.masonryFinishing },
        { workType: "MEP Works", description: wmrData.workExecuted.mepWorks },
        { workType: "External Development", description: wmrData.workExecuted.externalDevelopment }
      ]));

      // Add work in progress (merged)
      const workInProgressData = wmrData.workInProgress.mergedProgress.map(entry => ({
        activity: entry.activity || "",
        status: entry.status || "",
        location: entry.location || "",
        remarks: entry.remark || ""
      }));
      console.log('Frontend: Sending workInProgress data:', workInProgressData.length, 'items');
      formData.append('workInProgress', JSON.stringify(workInProgressData));

      // Add location progress (extracted from merged data)
      const locationProgressData = wmrData.workInProgress.mergedProgress
        .filter(entry => entry.location || entry.remark)
        .map(entry => ({
          locationArea: entry.location || "",
          progress: entry.remark || ""
        }));
      console.log('Frontend: Sending locationProgress data:', locationProgressData.length, 'items');
      formData.append('locationProgress', JSON.stringify(locationProgressData));

      // Add planned vs actual data
      const plannedVsActualData = [];
      weekDaysData.forEach(day => {
        day.entries.forEach(entry => {
          plannedVsActualData.push({
            dayDate: format(day.date, "yyyy-MM-dd"),
            contractorVendorName: entry.contractor || "",
            targetedActivityDetails: entry.activity || "",
            status: entry.status || "OPEN",
            varianceReason: entry.variance || "",
            expectedCompletionDate: entry.expectedCompletionDate ? format(entry.expectedCompletionDate, "yyyy-MM-dd") : null
          });
        });
      });
      console.log('Frontend: Sending plannedActualProgress data:', plannedVsActualData.length, 'items');
      formData.append('plannedActualProgress', JSON.stringify(plannedVsActualData));

      // Add manpower deployment - match backend schema (Group by labourType)
      const manpowerDataGroups = [];
      wmrData.manpowerDeployment.laborTypes.forEach(labor => {
        const dailyCount = {};
        labor.data.forEach((count, index) => {
          const date = wmrData.manpowerDeployment.dates[index];
          if (date) {
            // Ensure count is a number, default to 0 if invalid
            const numCount = parseInt(count) || 0;
            dailyCount[date] = numCount;
          }
        });

        manpowerDataGroups.push({
          labourType: labor.type,
          dailyCount: dailyCount
        });
      });
      console.log("Saving manpowerDataGroups:", manpowerDataGroups);
      formData.append('manpowerDeployment', JSON.stringify(manpowerDataGroups));

      // Add machinery equipment
      const machineryData = wmrData.machineryEquipment.map(machine => ({
        typeOfEquipment: machine.equipment || "",
        quantity: parseInt(machine.quantity) || 0,
        workingHours: parseInt(machine.hours) || 0,
        utilizationStatus: machine.utilization || "",
        equipmentId: machine.equipmentId || "",
        equipmentName: machine.equipmentName || "",
        equipmentType: machine.equipmentType || "",
        equipmentModel: machine.equipmentModel || "",
        equipmentMake: machine.equipmentMake || "",
        equipmentYear: machine.equipmentYear || "",
        equipmentCondition: machine.equipmentCondition || "",
        equipmentOperator: machine.equipmentOperator || "",
        equipmentOperatorContact: machine.equipmentOperatorContact || "",
        equipmentOperatorLicense: machine.equipmentOperatorLicense || "",
        equipmentOperatorExperience: machine.equipmentOperatorExperience || "",
        equipmentMaintenance: machine.equipmentMaintenance || "",
        equipmentMaintenanceSchedule: machine.equipmentMaintenanceSchedule || "",
        equipmentMaintenanceRecord: machine.equipmentMaintenanceRecord || "",
        equipmentInspection: machine.equipmentInspection || "",
        equipmentInspectionSchedule: machine.equipmentInspectionSchedule || "",
        equipmentInspectionRecord: machine.equipmentInspectionRecord || "",
      }));
      console.log('Frontend: Sending machineryEquipment data:', machineryData.length, 'items');
      formData.append('machineryEquipment', JSON.stringify(machineryData));

      // Add material status
      const materialData = wmrData.materialStatus.map(material => ({
        materialDescription: material.description || "",
        materialReceived: parseFloat(material.received) || 0,
        materialConsumed: parseFloat(material.consumed) || 0,
        balanceAtSite: parseFloat(material.balance) || 0,
        unit: material.unit || "",
        supplier: material.supplier || "",
        purchaseOrder: material.purchaseOrder || "",
        deliveryDate: material.deliveryDate || "",
        remarks: material.remarks || ""
      }));
      console.log('Frontend: Sending materialStatus data:', materialData.length, 'items');
      formData.append('materialStatus', JSON.stringify(materialData));

      // Add quality control activities
      const qualityData = wmrData.qualityControl.map(quality => ({
        inspectionCarriedOut: quality.inspection || "",
        testConducted: quality.test || "",
        ncrObservation: quality.observation || "",
        inspectionDate: quality.inspectionDate || "",
        testDate: quality.testDate || "",
        ncrDate: quality.ncrDate || "",
        status: quality.status || "OPEN"
      }));
      console.log('Frontend: Sending qualityControlActivities data:', qualityData.length, 'items');
      formData.append('qualityControlActivities', JSON.stringify(qualityData));

      // Add safety & HSE status
      formData.append('safetyHSEStatus', JSON.stringify(wmrData.safetyHSE));

      // Add issues/constraints
      formData.append('issuesConstraints', JSON.stringify(wmrData.issuesConstraints));

      // Add instructions/decisions
      const visitorData = wmrData.instructionsDecisions.map(visitor => ({
        date: format(visitor.date, "yyyy-MM-dd"),
        visitorName: visitor.visitorName || "",
        designation: visitor.designation || "",
        instructions: visitor.instruction || "",
        remarks: visitor.remark || ""
      }));
      formData.append('instructionsDecisions', JSON.stringify(visitorData));

      // Add decision discussion
      const decisionData = [{
        record: wmrData.decisionDiscussion || "",
        date: new Date().toISOString()
      }];
      formData.append('decisionRecords', JSON.stringify(decisionData));

      // Add plan for next week
      const planData = [
        {
          targetActivity: wmrData.planNextWeek.targetActivities || "",
          resourcePlanning: wmrData.planNextWeek.resourcePlanning || ""
        }
      ];
      formData.append('planNextWeek', JSON.stringify(planData));

      // Add report files
      uploadedReports.forEach((file, index) => {
        if (file instanceof File) {
          console.log('Frontend: Adding report file:', file);
          formData.append(`reportFiles`, file);
        }
      });

      console.log('Frontend: originalReportFiles:', originalReportFiles);
      console.log('Frontend: uploadedReports:', uploadedReports);

      const removedReportFiles = originalReportFiles.filter(originalFile =>
        !uploadedReports.some(currentFile =>
          currentFile.path === originalFile.path ||
          (currentFile instanceof File && currentFile.name === originalFile.name)
        )
      );

      console.log('Frontend: removedReportFiles:', removedReportFiles);

      if (removedReportFiles.length > 0) {
        console.log('Frontend: Adding removed report files information');
        formData.append('removedReportFiles', JSON.stringify(removedReportFiles));
      }

      // Handle removed photos
      console.log('Frontend: originalPhotos:', originalPhotos);
      console.log('Frontend: uploadedPhotos:', uploadedPhotos);
      console.log('Frontend: originalPhotos details:', JSON.stringify(originalPhotos.map(p => ({ photoPath: p.photoPath, name: p.name, description: p.description })), null, 2));
      console.log('Frontend: uploadedPhotos details:', JSON.stringify(uploadedPhotos.map(p => ({ photoPath: p.photoPath, name: p.name, description: p.description })), null, 2));

      const removedPhotos = originalPhotos.filter(originalPhoto =>
        !uploadedPhotos.some(currentPhoto =>
          (currentPhoto.photoPath && currentPhoto.photoPath === originalPhoto.photoPath) ||
          (currentPhoto.file && currentPhoto.name === originalPhoto.name)
        )
      );

      console.log('Frontend: removedPhotos:', removedPhotos);
      console.log('Frontend: removedPhotos details:', JSON.stringify(removedPhotos.map(p => ({ photoPath: p.photoPath, name: p.name, description: p.description })), null, 2));

      if (removedPhotos.length > 0) {
        console.log('Frontend: Adding removed photos information');
        formData.append('removedPhotographs', JSON.stringify(removedPhotos));
      }

      // Add photographs with shapes data
      console.log("Frontend: Adding photographs to FormData:", uploadedPhotos.length);
      let fileIndex = 0; // Use sequential index for files being uploaded
      uploadedPhotos.forEach((photo, index) => {
        if (photo.file) {
          console.log('Frontend: Adding photograph:', photo.file);
          console.log(`Frontend: Photo ${fileIndex} details:`, {
            title: photo.title,
            titleLength: photo.title?.length,
            fileName: photo.file.name,
            shapesCount: photo.shapes?.length || 0
          });
          formData.append(`photographs`, photo.file);
          // Add shapes data as separate field using fileIndex
          formData.append(`photo_${fileIndex}_shapes`, JSON.stringify(photo.shapes || []));
          formData.append(`photo_${fileIndex}_title`, photo.title || '');
          console.log(`Frontend: Appended photo_${fileIndex}_title:`, photo.title || '');
          fileIndex++; // Increment only for files being uploaded
        }
      });

      // Add project layout image if present
      if (projectLayoutImage) {
        formData.append('projectLayoutImage', projectLayoutImage);
      }

      // Handle project layout image removal
      if (originalProjectLayoutImage && !projectLayoutImage && !projectLayoutImagePreview) {
        formData.append('removeProjectLayoutImage', 'true');
      }

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          'Content-Type': 'multipart/form-data',
        },
      };

      // Update WMR
      const response = await axios.put(
        `${url}/api/wmr/update/${wmrId}`,
        formData,
        config
      );

      if (response.data.message) {
        showToast("success", "Success", "Weekly Monitoring Report updated successfully!");
        setTimeout(() => navigate(-1), 1500);
      } else {
        showToast("error", "Error", response.data.message || "Failed to update WMR.");
      }
    } catch (err) {
      console.error("Error updating WMR:", err);
      showToast("error", "Error", err.response?.data?.message || "Failed to update report.");
    } finally {
      setSaving(false);
    }
  };

  // Show toast function
  const showToast = (type, title, message) => {
    setToast({
      open: true,
      type,
      title,
      message,
    });
  };

  const closeToast = () => {
    setToast({ ...toast, open: false });
  };

  // Handle numeric input validation
  const handleNumericInput = (e, setterFunction) => {
    const value = e.target.value;
    // Allow only numbers and empty string
    if (value === '' || /^\d*$/.test(value)) {
      setterFunction(value);
    }
  };

  // Handle cut operation for 0 values
  const handleCutOperation = (e, setterFunction) => {
    setTimeout(() => {
      if (e.target.value === '0') {
        setterFunction('');
      }
    }, 0);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  if (!project) {
    return (
      <Box p={2}>
        <Alert severity="warning">Project not found</Alert>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  const manpowerValues = calculateManpowerValues();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box p={2} sx={{ fontFamily: FONT_FAMILY }}>
        {/* ================= HEADER ================= */}
        <Box mb={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Typography sx={{ ...TYPOGRAPHY.H2, color: COLORS.PRIMARY, fontFamily: FONT_FAMILY }}>
                Edit Weekly Monitoring Report (WMR)
              </Typography>

              <Button
                onClick={handleDownloadReport}
                disabled={downloading || !weekStartDate}
                startIcon={
                  downloading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <Icon name="download1" />
                  )
                }
                sx={{
                  backgroundColor: COLORS.SECONDARY,
                  color: "#fff",
                  textTransform: "none",
                  borderRadius: "7px",
                  px: 2,
                  height: 36,
                  "&:hover": {
                    backgroundColor: COLORS.PRIMARY,
                  },
                }}
              >
                {downloading ? "Downloading..." : "Download"}
              </Button>
            </Box>

            <IconButton size="small" onClick={() => navigate(-1)}>
              <Icon name="close3" color={COLORS.ERROR} />
            </IconButton>
          </Box>

          <Box display="flex" flexDirection="column" gap={0.4}>
            <Box display="flex" justifyContent="space-between" flexWrap={isMobile ? "wrap" : "nowrap"} gap={1}>
              <Typography sx={{ ...TYPOGRAPHY.BODY_STANDARD, fontFamily: FONT_FAMILY }}>
                Project Name:{" "}
                <span style={{ color: COLORS.SECONDARY }}>
                  {propProjectName || project.projectName}
                </span>
              </Typography>

              <Box display="flex" alignItems="center" gap={1} flexWrap={isMobile ? "wrap" : "nowrap"}>
                <Typography sx={{ ...TYPOGRAPHY.BODY_STANDARD, fontFamily: FONT_FAMILY }}>Week:</Typography>

                {weekStartDate && weekEndDate && (
                  <Typography sx={{ ...TYPOGRAPHY.BODY_STANDARD, fontFamily: FONT_FAMILY, color: COLORS.SECONDARY }}>
                    {formatDateForWeekHeader(weekStartDate)} - {formatDateForWeekHeader(weekEndDate)}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box display="flex" justifyContent="space-between" flexWrap={isMobile ? "wrap" : "nowrap"} gap={1}>
              <Typography sx={{ ...TYPOGRAPHY.BODY_STANDARD, fontFamily: FONT_FAMILY }}>
                Address:{" "}
                <span style={{ color: COLORS.SECONDARY }}>
                  {project.siteAddress || "N/A"}
                </span>
              </Typography>

              <Typography sx={{ ...TYPOGRAPHY.BODY_STANDARD, fontFamily: FONT_FAMILY }}>
                PMC:{" "}
                <span style={{ color: COLORS.SECONDARY }}>
                  {companies.find(c => c._id === selectedCompany)?.name || "N/A"}
                </span>
              </Typography>
            </Box>

            <Box display="flex" justifyContent="space-between" flexWrap={isMobile ? "wrap" : "nowrap"} gap={1}>
              <Typography sx={{ ...TYPOGRAPHY.BODY_STANDARD, fontFamily: FONT_FAMILY }}>
                Developer:{" "}
                <span style={{ color: COLORS.SECONDARY }}>
                  {project.developerName || "N/A"}
                </span>
              </Typography>

              <Typography sx={{ ...TYPOGRAPHY.BODY_STANDARD, fontFamily: FONT_FAMILY }}>
                Engineer:{" "}
                <span style={{ color: COLORS.SECONDARY }}>
                  {project.assignedEngineer?.name || project.assignedEngineerName || "Not Assigned"}
                </span>
              </Typography>
            </Box>
          </Box>

          {/* HEADER BOTTOM LINE */}
          <Box
            mt={1.5}
            height="1px"
            sx={{ backgroundColor: COLORS.LIGHT_SECONDARY }}
          />
        </Box>

        {/* Show warning if week not selected */}
        {!weekStartDate && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Please select a week date to start adding entries
          </Alert>
        )}

        {/* ================= 1. PROJECT DETAILS ================= */}
        {weekStartDate && (
          <>
            {/* ================= STAKEHOLDERS ================= */}
            <Typography sx={{ ...TYPOGRAPHY.H4, mb: 1, mt: 2, fontFamily: FONT_FAMILY, color: COLORS.TEXT }}>
              Stakeholders
            </Typography>

            <Box
              display="flex"
              sx={{
                borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                py: 0.8,
                ...TYPOGRAPHY.H4,
                color: COLORS.TEXT,
                fontFamily: FONT_FAMILY,
              }}
            >
              <Box width="20%" px={1}>Role</Box>
              <Box width="30%" px={1}>Name</Box>
              <Box width="50%" px={1}>Description</Box>
            </Box>

            {project?.stakeholders?.map((stakeholder, i) => (
              <Box key={i} display="flex" sx={{ py: 1, borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
                <Box width="20%" px={1} sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                  {stakeholder.role || "N/A"}
                </Box>
                <Box width="30%" px={1} sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                  {stakeholder.name || "N/A"}
                </Box>
                <Box width="50%" px={1} sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                  {stakeholder.details || "N/A"}
                </Box>
              </Box>
            ))}

            {/* ================= PROJECT LAYOUT IMAGE ================= */}
            <Typography sx={{ ...TYPOGRAPHY.H4, mb: 1, mt: 2, fontFamily: FONT_FAMILY, color: COLORS.TEXT }}>
              Project Layout Image
            </Typography>

            <Box
              sx={{
                border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                borderRadius: 1,
                p: 2,
                mb: 2
              }}
            >
              {projectLayoutImagePreview || originalProjectLayoutImage ? (
                <Box>
                  <Box
                    component="img"
                    src={projectLayoutImagePreview || (() => {
                      if (originalProjectLayoutImage?.filePath) {
                        const imagePath = originalProjectLayoutImage.filePath.replace(/\\/g, '/');
                        return imagePath.startsWith('/') ? `${url}${imagePath}` : `${url}/${imagePath}`;
                      }
                      return '';
                    })()}
                    alt="Project Layout"
                    sx={{
                      width: "100%",
                      maxHeight: "300px",
                      objectFit: "contain",
                      borderRadius: 1,
                      mb: 1
                    }}
                  />
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.SECONDARY }}>
                      {projectLayoutImage?.name || originalProjectLayoutImage?.fileName}
                    </Typography>
                    <Box display="flex" gap={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<Icon name="download1" size={16} />}
                        onClick={downloadProjectLayoutImage}
                        disabled={downloading}
                      >
                        {downloading ? "Downloading..." : "Download"}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<Icon name="trash" size={16} />}
                        onClick={removeProjectLayoutImage}
                      >
                        Remove
                      </Button>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    border: `2px dashed ${COLORS.LIGHT_SECONDARY}`,
                    borderRadius: 1,
                    p: 3,
                    textAlign: "center",
                    cursor: "pointer",
                    "&:hover": {
                      borderColor: COLORS.PRIMARY,
                      backgroundColor: "rgba(0, 0, 0, 0.02)"
                    }
                  }}
                  onClick={() => document.getElementById('edit-project-layout-input').click()}
                >
                  <Icon name="upload" size={32} color={COLORS.LIGHT_SECONDARY} />
                  <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, mt: 1, color: COLORS.SECONDARY }}>
                    Click to upload project layout image
                  </Typography>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, mt: 0.5, color: COLORS.LIGHT_SECONDARY }}>
                    Supported formats: JPG, PNG, GIF (Max 10MB)
                  </Typography>
                  <input
                    id="edit-project-layout-input"
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleProjectLayoutImageUpload}
                  />
                </Box>
              )}
            </Box>

            {/* ================= 2. WORK EXECUTED ================= */}
            <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.PRIMARY, mt: 4, mb: 1.5, fontFamily: FONT_FAMILY }}>
              2. Work Executed During the Week:
            </Typography>

            {/* RCC Work */}
            <Box mb={2.5}>
              <Typography sx={{ ...TYPOGRAPHY.H4, mb: 0.8, fontFamily: FONT_FAMILY, color: COLORS.PRIMARY }}>
                RCC Work
              </Typography>
              <RichTextEditor
                value={wmrData.workExecuted.rccWork}
                onChange={(value) => setWmrData(prev => ({
                  ...prev,
                  workExecuted: { ...prev.workExecuted, rccWork: value }
                }))}
                placeholder="Describe RCC work executed during the week..."
              />
            </Box>

            {/* Civil Works */}
            <Box mb={2.5}>
              <Typography sx={{ ...TYPOGRAPHY.H4, mb: 0.8, fontFamily: FONT_FAMILY, color: COLORS.PRIMARY }}>
                Civil Works
              </Typography>
              <RichTextEditor
                value={wmrData.workExecuted.civilWorks}
                onChange={(value) => setWmrData(prev => ({
                  ...prev,
                  workExecuted: { ...prev.workExecuted, civilWorks: value }
                }))}
                placeholder="Describe civil works executed during the week..."
              />
            </Box>

            {/* Masonry / Finishing Works */}
            <Box mb={2.5}>
              <Typography sx={{ ...TYPOGRAPHY.H4, mb: 0.8, fontFamily: FONT_FAMILY, color: COLORS.PRIMARY }}>
                Masonry / Finishing Works
              </Typography>
              <RichTextEditor
                value={wmrData.workExecuted.masonryFinishing}
                onChange={(value) => setWmrData(prev => ({
                  ...prev,
                  workExecuted: { ...prev.workExecuted, masonryFinishing: value }
                }))}
                placeholder="Describe masonry/finishing works executed during the week..."
              />
            </Box>

            {/* MEP Works */}
            <Box mb={2.5}>
              <Typography sx={{ ...TYPOGRAPHY.H4, mb: 0.8, fontFamily: FONT_FAMILY, color: COLORS.PRIMARY }}>
                MEP Works
              </Typography>
              <RichTextEditor
                value={wmrData.workExecuted.mepWorks}
                onChange={(value) => setWmrData(prev => ({
                  ...prev,
                  workExecuted: { ...prev.workExecuted, mepWorks: value }
                }))}
                placeholder="Describe MEP works executed during the week..."
              />
            </Box>

            {/* External Development */}
            <Box mb={2.5}>
              <Typography sx={{ ...TYPOGRAPHY.H4, mb: 0.8, fontFamily: FONT_FAMILY, color: COLORS.PRIMARY }}>
                External Development
              </Typography>
              <RichTextEditor
                value={wmrData.workExecuted.externalDevelopment}
                onChange={(value) => setWmrData(prev => ({
                  ...prev,
                  workExecuted: { ...prev.workExecuted, externalDevelopment: value }
                }))}
                placeholder="Describe external development work executed during the week..."
              />
            </Box>

            {/* ================= 3. WORK IN PROGRESS ================= */}
            <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.PRIMARY, mt: 4, mb: 1.5, fontFamily: FONT_FAMILY }}>
              3. Work in Progress (WIP):
            </Typography>

            {/* Merged Work Progress Table */}
            <Box mb={3}>
              {/* Table Header */}
              <Box
                display="flex"
                sx={{
                  borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                  py: 0.8,
                }}
              >
                <Box flex={1} sx={{ ...TYPOGRAPHY.H4, fontFamily: FONT_FAMILY, color: COLORS.TEXT }}>
                  Activity
                </Box>
                <Box flex={1} sx={{ ...TYPOGRAPHY.H4, fontFamily: FONT_FAMILY, color: COLORS.TEXT }}>
                  Location
                </Box>
                <Box flex={1} sx={{ ...TYPOGRAPHY.H4, fontFamily: FONT_FAMILY, color: COLORS.TEXT }}>
                  Status
                </Box>
                <Box flex={1} sx={{ ...TYPOGRAPHY.H4, fontFamily: FONT_FAMILY, color: COLORS.TEXT }}>
                  Remark
                </Box>
                <Box width="120px" sx={{ ...TYPOGRAPHY.H4, fontFamily: FONT_FAMILY, color: COLORS.TEXT, textAlign: "center" }}>
                  Actions
                </Box>
              </Box>

              {/* Table Body */}
              {wmrData.workInProgress.mergedProgress.map((row, i) => (
                <Box
                  key={i}
                  display="flex"
                  sx={{
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    py: 0.8,
                  }}
                >
                  <Box flex={1} sx={{ ...TYPOGRAPHY.BODY_LARGE, fontFamily: FONT_FAMILY, color: COLORS.SECONDARY }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={row.activity}
                      onChange={(e) => {
                        const updatedProgress = [...wmrData.workInProgress.mergedProgress];
                        updatedProgress[i].activity = e.target.value;
                        setWmrData(prev => ({
                          ...prev,
                          workInProgress: {
                            mergedProgress: updatedProgress
                          }
                        }));
                      }}
                      multiline
                      maxRows={3}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                        },
                      }}
                    />
                  </Box>
                  <Box flex={1} sx={{ ...TYPOGRAPHY.BODY_LARGE, fontFamily: FONT_FAMILY, color: COLORS.SECONDARY }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={row.location}
                      onChange={(e) => {
                        const updatedProgress = [...wmrData.workInProgress.mergedProgress];
                        updatedProgress[i].location = e.target.value;
                        setWmrData(prev => ({
                          ...prev,
                          workInProgress: {
                            mergedProgress: updatedProgress
                          }
                        }));
                      }}
                      multiline
                      maxRows={3}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                        },
                      }}
                    />
                  </Box>
                  <Box flex={1} sx={{ ...TYPOGRAPHY.BODY_LARGE, fontFamily: FONT_FAMILY, color: COLORS.SECONDARY }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={row.status}
                      onChange={(e) => {
                        const updatedProgress = [...wmrData.workInProgress.mergedProgress];
                        updatedProgress[i].status = e.target.value;
                        setWmrData(prev => ({
                          ...prev,
                          workInProgress: {
                            mergedProgress: updatedProgress
                          }
                        }));
                      }}
                      multiline
                      maxRows={3}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                        },
                      }}
                    />
                  </Box>
                  <Box flex={1} sx={{ ...TYPOGRAPHY.BODY_LARGE, fontFamily: FONT_FAMILY, color: COLORS.SECONDARY }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={row.remark}
                      onChange={(e) => {
                        const updatedProgress = [...wmrData.workInProgress.mergedProgress];
                        updatedProgress[i].remark = e.target.value;
                        setWmrData(prev => ({
                          ...prev,
                          workInProgress: {
                            mergedProgress: updatedProgress
                          }
                        }));
                      }}
                      multiline
                      maxRows={3}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                        },
                      }}
                    />
                  </Box>
                  <Box width="120px" sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setNewProgressEntry({
                          activity: row.activity,
                          location: row.location,
                          status: row.status,
                          remark: row.remark
                        });
                        setShowActivityForm(true);
                        setEditingEntry({
                          section: 'progress',
                          index: i,
                          data: row
                        });
                      }}
                      sx={{ color: COLORS.PRIMARY }}
                    >
                      <Icon name="Action3" size={16} color="#8CD47E" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteProgressEntry(i)}
                      sx={{ color: COLORS.ERROR }}
                    >
                      <Icon name="close2" size={16} />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Add New Entry Button */}
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button
                variant="outlined"
                startIcon={<Icon name="plus2" />}
                sx={addBtnStyle}
                onClick={() => {
                  setNewProgressEntry({ activity: "", location: "", status: "", remark: "" });
                  setShowActivityForm(true);
                  setEditingEntry({
                    section: null,
                    index: null,
                    data: null
                  });
                }}
              >
                Add New Entry
              </Button>
            </Box>

            {/* Input Form */}
            {showActivityForm && (
              <Box py={2} sx={{ border: `1px solid ${COLORS.LIGHT_SECONDARY}`, borderRadius: "8px", p: 2, mt: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography sx={{ fontFamily: FONT_FAMILY, color: COLORS.PRIMARY, ...TYPOGRAPHY.H4 }}>
                    {editingEntry.section === 'progress' ? "Edit Progress Entry" : "Add New Progress Entry"}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setShowActivityForm(false);
                      setNewProgressEntry({ activity: "", location: "", status: "", remark: "" });
                      setEditingEntry({
                        section: null,
                        index: null,
                        data: null
                      });
                    }}
                    sx={{ color: COLORS.ERROR }}
                  >
                    <Icon name="close2" />
                  </IconButton>
                </Box>
                <Box display="flex" gap={2} mb={2}>
                  <Box flex={1}>
                    <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 0.5, fontFamily: FONT_FAMILY }}>
                      Activity
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Activity"
                      size="small"
                      value={newProgressEntry.activity}
                      onChange={(e) => setNewProgressEntry({ ...newProgressEntry, activity: e.target.value })}
                      sx={inputStyle}
                      multiline
                      maxRows={3}
                    />
                  </Box>
                  <Box flex={1}>
                    <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 0.5, fontFamily: FONT_FAMILY }}>
                      Location
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Location"
                      size="small"
                      value={newProgressEntry.location}
                      onChange={(e) => setNewProgressEntry({ ...newProgressEntry, location: e.target.value })}
                      sx={inputStyle}
                      multiline
                      maxRows={3}
                    />
                  </Box>
                </Box>
                <Box display="flex" gap={1} mb={2}>
                  <Box flex={1}>
                    <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 0.5, fontFamily: FONT_FAMILY }}>
                      Status
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Status"
                      size="small"
                      value={newProgressEntry.status}
                      onChange={(e) => setNewProgressEntry({ ...newProgressEntry, status: e.target.value })}
                      sx={inputStyle}
                      multiline
                      maxRows={3}
                    />
                  </Box>
                  <Box flex={1}>
                    <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 0.5, fontFamily: FONT_FAMILY }}>
                      Remark
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Remark"
                      size="small"
                      value={newProgressEntry.remark}
                      onChange={(e) => setNewProgressEntry({ ...newProgressEntry, remark: e.target.value })}
                      sx={inputStyle}
                      multiline
                      maxRows={3}
                    />
                  </Box>
                </Box>
                <Box display="flex" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    startIcon={<Icon name="plus2" />}
                    sx={addBtnStyle}
                    onClick={handleAddProgressEntry}
                  >
                    {editingEntry.section === 'progress' ? "Update Entry" : "Add Entry"}
                  </Button>
                </Box>
              </Box>
            )}

            {/* ================= 4. PLANNED VS ACTUAL PROGRESS ================= */}
            <Box mt={4}>
              <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.PRIMARY, fontFamily: FONT_FAMILY, mb: 1.5 }}>
                4. Planned vs Actual Progress:
              </Typography>

              {/* Table Header */}
              <Box
                display="flex"
                sx={{
                  py: 1.2,
                  ...TYPOGRAPHY.H4,
                  color: COLORS.TEXT,
                  fontFamily: FONT_FAMILY,
                  borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                  alignItems: 'center'
                }}
              >
                <FlexCell w="160px" borderBottom="none" sx={{ display: 'flex', alignItems: 'center' }}>
                  Week Days
                </FlexCell>
                <FlexCell flex={1} sx={{ display: 'flex', alignItems: 'center' }}>
                  Contractor/Vendor
                </FlexCell>
                <FlexCell flex={1.4} sx={{ display: 'flex', alignItems: 'center' }}>
                  Targeted Activity Details
                </FlexCell>
                <FlexCell w="100px" sx={{ display: 'flex', alignItems: 'center' }}>
                  Status
                </FlexCell>
                <FlexCell flex={1.2} sx={{ display: 'flex', alignItems: 'center' }}>
                  Variance/Reason
                </FlexCell>
                <FlexCell w="140px" sx={{ display: 'flex', alignItems: 'center' }}>
                  Expected Completion Date
                </FlexCell>
                <FlexCell w="120px" borderBottom="none" sx={{ display: 'flex', alignItems: 'center' }}>
                  Actions
                </FlexCell>
              </Box>

              {/* Table Body */}
              {console.log("Rendering weekDaysData:", weekDaysData) ||
                weekDaysData.map((dayBlock, dayIndex) => (
                  <Box key={dayIndex}>
                    {console.log(`Rendering day ${dayIndex} with entries:`, dayBlock.entries) ||
                      dayBlock.entries.map((row, entryIndex) => (
                        <FlexRow key={entryIndex}>
                          {/* WEEK COLUMN */}
                          <FlexCell w="160px" sx={{ height: "100%", display: "flex", alignItems: "center" }}>
                            {entryIndex === 0 && (
                              <Typography
                                sx={{
                                  ...TYPOGRAPHY.BODY_LARGE,
                                  color: COLORS.SECONDARY,
                                  wordBreak: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                {dayBlock.day.split(" ")[0]}
                                <br />
                                {dayBlock.formattedDate.split(" ").slice(1).join(" ")}
                              </Typography>
                            )}
                          </FlexCell>

                          {/* DATA COLUMNS */}
                          <FlexRowInner>
                            <FlexCell flex={1} sx={{ display: "flex", alignItems: "center" }}>
                              <Typography sx={{
                                color: COLORS.SECONDARY,
                                ...TYPOGRAPHY.BODY_LARGE,
                                wordWrap: "break-word",
                                lineHeight: 1.4,
                                whiteSpace: "normal",
                              }}>
                                {row.contractor}
                              </Typography>
                            </FlexCell>
                            <FlexCell flex={1.4} sx={{ display: "flex", alignItems: "center" }}>
                              <Typography sx={{
                                color: COLORS.SECONDARY,
                                ...TYPOGRAPHY.BODY_LARGE,
                                wordWrap: "break-word",
                                lineHeight: 1.4,
                                whiteSpace: "normal",
                              }}>
                                {row.activity}
                              </Typography>
                            </FlexCell>
                            <FlexCell w="100px" sx={{ display: "flex", alignItems: "center" }}>
                              <Typography sx={{
                                color: COLORS.SECONDARY,
                                ...TYPOGRAPHY.BODY_LARGE,
                                wordWrap: "break-word",
                                lineHeight: 1.4,
                                fontWeight: row.status === "OPEN" ? "bold" : "normal",
                                color: row.status === "OPEN" ? COLORS.PRIMARY : COLORS.SECONDARY
                              }}>
                                {row.status || "OPEN"}
                              </Typography>
                            </FlexCell>
                            <FlexCell flex={1.2} sx={{ display: "flex", alignItems: "center" }}>
                              <Typography sx={{
                                color: COLORS.SECONDARY,
                                ...TYPOGRAPHY.BODY_LARGE,
                                wordWrap: "break-word",
                                lineHeight: 1.4,
                                whiteSpace: "pre-wrap"
                              }}>
                                {row.variance || "-"}
                              </Typography>
                            </FlexCell>
                            <FlexCell w="140px" sx={{ display: "flex", alignItems: "center" }}>
                              <Typography sx={{
                                color: COLORS.SECONDARY,
                                ...TYPOGRAPHY.BODY_LARGE,
                                wordWrap: "break-word",
                                lineHeight: 1.4,
                                whiteSpace: "normal"
                              }}>
                                {row.expectedCompletionDate ? format(row.expectedCompletionDate, "dd/MM/yyyy") : "-"}
                              </Typography>
                            </FlexCell>
                          </FlexRowInner>

                          {/* EDIT AND DELETE BUTTONS */}
                          <FlexCell w="120px" sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                // Open edit modal with pre-filled data
                                setNewEntry({ ...row });
                                setOpenDay(dayIndex);
                                setEditingEntry({
                                  section: 'plannedActual',
                                  dayIndex: dayIndex,
                                  entryIndex: entryIndex,
                                  data: row
                                });
                              }}
                              sx={{ color: COLORS.PRIMARY }}
                            >
                              <Icon name="Action3" size={16} color="#8CD47E" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeletePlannedVsActualEntry(dayIndex, entryIndex)}
                              sx={{ color: COLORS.ERROR }}
                            >
                              <Icon name="close2" size={16} />
                            </IconButton>
                          </FlexCell>
                        </FlexRow>
                      ))}

                    {/* Show empty row with date when no entries */}
                    {dayBlock.entries.length === 0 && (
                      <FlexRow>
                        <FlexCell w="160px" sx={{ height: "100%", display: "flex", alignItems: "center" }}>
                          <Typography
                            sx={{
                              ...TYPOGRAPHY.BODY_LARGE,
                              color: COLORS.SECONDARY,
                              wordBreak: "break-word",
                              whiteSpace: "normal",
                            }}
                          >
                            {dayBlock.day.split(" ")[0]}
                            <br />
                            {dayBlock.formattedDate.split(" ").slice(1).join(" ")}
                          </Typography>
                        </FlexCell>
                        <FlexRowInner>
                          <FlexCell flex={1} sx={{ display: "flex", alignItems: "center" }} />
                          <FlexCell flex={1.4} sx={{ display: "flex", alignItems: "center" }} />
                          <FlexCell w="100px" sx={{ display: "flex", alignItems: "center" }} />
                          <FlexCell flex={1.2} sx={{ display: "flex", alignItems: "center" }} />
                          <FlexCell w="140px" sx={{ display: "flex", alignItems: "center" }} />
                        </FlexRowInner>
                        <FlexCell w="120px" sx={{ display: "flex", alignItems: "center" }} />
                      </FlexRow>
                    )}

                    {/* ADD/EDIT ENTRY FORM - Only show when openDay matches */}
                    {openDay === dayIndex && (
                      <Box py={2} sx={{ border: `1px solid ${COLORS.LIGHT_SECONDARY}`, borderRadius: "8px", p: 2, mt: 1 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography sx={{ fontFamily: FONT_FAMILY, color: COLORS.PRIMARY, ...TYPOGRAPHY.H4 }}>
                            {editingEntry.section === 'plannedActual' ? "Edit Entry" : "Add New Entry"} for {dayBlock.formattedDate}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setOpenDay(null);
                              setNewEntry({ contractor: "", activity: "", status: "OPEN", variance: "", expectedCompletionDate: null });
                              setEditingEntry({
                                section: null,
                                dayIndex: null,
                                entryIndex: null,
                                data: null
                              });
                            }}
                            sx={{ color: COLORS.ERROR }}
                          >
                            <Icon name="close2" />
                          </IconButton>
                        </Box>
                        <Box display="flex" gap={2} mb={2}>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Contractor / Vendor"
                            value={newEntry.contractor}
                            onChange={(e) => setNewEntry({ ...newEntry, contractor: e.target.value })}
                            sx={inputStyle}
                          />
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Targeted Activity Details"
                            value={newEntry.activity}
                            onChange={(e) => setNewEntry({ ...newEntry, activity: e.target.value })}
                            sx={inputStyle}
                          />
                        </Box>
                        <Box display="flex" gap={2}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            value={newEntry.status || "OPEN"}
                            onChange={(e) => setNewEntry({ ...newEntry, status: e.target.value })}
                            sx={inputStyle}
                            SelectProps={{
                              MenuProps: {
                                PaperProps: {
                                  sx: {
                                    maxHeight: 200,
                                  },
                                },
                              },
                            }}
                          >
                            <MenuItem value="OPEN">Open</MenuItem>
                            <MenuItem value="CLOSED">Closed</MenuItem>
                          </TextField>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Variance / Reason"
                            value={newEntry.variance}
                            onChange={(e) => setNewEntry({ ...newEntry, variance: e.target.value })}
                            sx={inputStyle}
                          />
                          <TextField
                            fullWidth
                            size="small"
                            type="date"
                            placeholder="Expected Completion Date"
                            value={newEntry.expectedCompletionDate ? format(newEntry.expectedCompletionDate, "yyyy-MM-dd") : ""}
                            onChange={(e) => setNewEntry({ ...newEntry, expectedCompletionDate: e.target.value ? new Date(e.target.value) : null })}
                            sx={inputStyle}
                          />
                        </Box>
                        <Box display="flex" justifyContent="flex-end" mt={3}>
                          <Button
                            variant="contained"
                            startIcon={<Icon name="plus2" />}
                            sx={{
                              ...addBtnStyle,
                              backgroundColor: editingEntry.section === 'plannedActual' ? COLORS.PRIMARY : COLORS.SECONDARY,
                            }}
                            onClick={() => handleAddPlannedVsActualEntry(dayIndex)}
                          >
                            {editingEntry.section === 'plannedActual' ? "Update Entry" : "Add New Entry"}
                          </Button>
                        </Box>
                      </Box>
                    )}

                    {/* ADD BUTTON - Only show when not in add/edit mode */}
                    {openDay !== dayIndex && (
                      <FlexEnd mt={1}>
                        <Button
                          size="small"
                          startIcon={<Icon name="plus2" />}
                          sx={{
                            backgroundColor: COLORS.SECONDARY,
                            color: "#fff",
                            textTransform: "none",
                          }}
                          onClick={() => {
                            setNewEntry({ contractor: "", activity: "", status: "OPEN", variance: "", expectedCompletionDate: null });
                            setOpenDay(dayIndex);
                            setEditingEntry({
                              section: null,
                              dayIndex: null,
                              entryIndex: null,
                              data: null
                            });
                          }}
                        >
                          Add New Entry
                        </Button>
                      </FlexEnd>
                    )}

                    {/* DAY SEPARATOR */}
                    <Box
                      mt={2}
                      height="1px"
                      sx={{ backgroundColor: COLORS.LIGHT_SECONDARY }}
                    />
                  </Box>
                ))}
            </Box>

            {/* ================= 5. MANPOWER DEPLOYMENT ================= */}
            <Box mt={5}>
              <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.PRIMARY, fontFamily: FONT_FAMILY, mb: 1.5 }}>
                5. Manpower Deployment:
              </Typography>

              {/* Header Row */}
              <Box display="flex" gap={0.5} sx={{ borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`, pb: 1, mb: 2, ...TYPOGRAPHY.H4, fontFamily: FONT_FAMILY }}>
                <Box width="180px" pl={2} sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT, fontFamily: FONT_FAMILY }}>
                  Labour Type
                </Box>
                {wmrData.manpowerDeployment.dates.map((date, i) => (
                  <Box key={i} width="90px" textAlign="center" sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT, fontFamily: FONT_FAMILY }}>
                    {date}
                  </Box>
                ))}
                <Box width="90px" textAlign="center" sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT, fontFamily: FONT_FAMILY }}>
                  Average
                </Box>
                <Box width="90px" textAlign="center" sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT, fontFamily: FONT_FAMILY }}>
                  Total
                </Box>
                <Box width="60px" textAlign="center" sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT, fontFamily: FONT_FAMILY }}>
                  Actions
                </Box>
              </Box>

              {/* Data Rows */}
              {wmrData.manpowerDeployment.laborTypes.map((labor, typeIndex) => (
                <Box key={typeIndex} display="flex" gap={0.5} sx={{ mb: 2 }}>
                  <Box width="180px" sx={cellStyle}>
                    <TextField
                      fullWidth
                      size="small"
                      value={labor.type}
                      onChange={(e) => {
                        const updatedLaborTypes = [...wmrData.manpowerDeployment.laborTypes];
                        updatedLaborTypes[typeIndex].type = e.target.value;
                        setWmrData(prev => ({
                          ...prev,
                          manpowerDeployment: {
                            ...prev.manpowerDeployment,
                            laborTypes: updatedLaborTypes
                          }
                        }));
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: '44px',
                          '& fieldset': { border: 'none' }
                        },
                      }}
                    />
                  </Box>
                  {labor.data.map((value, dayIndex) => (
                    <Box key={dayIndex} width="90px" sx={cellStyle}>
                      <TextField
                        type="text"
                        variant="outlined"
                        size="small"
                        placeholder="0"
                        value={value}
                        onChange={(e) => handleNumericInput(e, (val) => handleManpowerChange(typeIndex, dayIndex, val))}
                        onCut={(e) => handleCutOperation(e, () => handleManpowerChange(typeIndex, dayIndex, ''))}
                        inputProps={{
                          style: { textAlign: 'center', padding: '4px 8px', height: '24px' },
                          inputMode: 'numeric',
                          pattern: '[0-9]*'
                        }}
                        sx={{
                          width: '100%',
                          '& .MuiOutlinedInput-root': { height: '44px', borderRadius: '8px', '& fieldset': { border: 'none' } },
                          '& input[type=number]': { MozAppearance: 'textfield' },
                        }}
                      />
                    </Box>
                  ))}
                  <Box width="90px" sx={{ ...cellStyle, display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, fontFamily: FONT_FAMILY, textAlign: 'center' }}>
                      {manpowerValues.averages[labor.type] || 0}
                    </Typography>
                  </Box>
                  <Box width="90px" sx={{ ...cellStyle, display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, fontFamily: FONT_FAMILY, textAlign: 'center' }}>
                      {manpowerValues.totals[labor.type] || 0}
                    </Typography>
                  </Box>
                  <Box width="60px" sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const updatedLaborTypes = [...wmrData.manpowerDeployment.laborTypes];
                        updatedLaborTypes.splice(typeIndex, 1);
                        setWmrData(prev => ({
                          ...prev,
                          manpowerDeployment: {
                            ...prev.manpowerDeployment,
                            laborTypes: updatedLaborTypes
                          }
                        }));
                      }}
                      sx={{ color: COLORS.ERROR }}
                    >
                      <Icon name="close2" size={16} />
                    </IconButton>
                  </Box>
                </Box>
              ))}

              {/* Add Button */}
              <Box display="flex" justifyContent="flex-end" mt={1.5}>
                <Button variant="outlined" startIcon={<Icon name="plus2" />} sx={addBtnStyle} onClick={handleAddLaborType}>
                  Add New Entry
                </Button>
              </Box>

              {/* Total Row */}
              <Box mt={3} pt={2} display="flex" gap={0.5} sx={{ borderTop: `2px solid ${COLORS.LIGHT_SECONDARY}` }}>
                <Box width="180px" sx={{ ...cellStyle, display: "flex", alignItems: "center" }}>
                  <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, fontFamily: FONT_FAMILY, pl: 2 }}>
                    Total
                  </Typography>
                </Box>
                {manpowerValues.columnTotals.map((total, i) => (
                  <Box key={i} width="90px" sx={{ ...cellStyle, display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, fontFamily: FONT_FAMILY, textAlign: 'center' }}>
                      {total}
                    </Typography>
                  </Box>
                ))}
                <Box width="90px" sx={{ ...cellStyle, display: "flex", justifyContent: "center", alignItems: "center" }}>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, fontFamily: FONT_FAMILY, textAlign: 'center' }}>
                    {manpowerValues.grandAverage}
                  </Typography>
                </Box>
                <Box width="90px" sx={{ ...cellStyle, display: "flex", justifyContent: "center", alignItems: "center" }}>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, fontFamily: FONT_FAMILY, textAlign: 'center' }}>
                    {manpowerValues.grandTotal}
                  </Typography>
                </Box>
                <Box width="60px" />
              </Box>
            </Box>

            {/* ================= 6. MACHINERY & EQUIPMENTS ================= */}
            <Box mt={5}>
              <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.PRIMARY, fontFamily: FONT_FAMILY, mb: 1.5 }}>
                6. Machinery & Equipments:
              </Typography>

              {/* Table Header */}
              <Box display="flex" sx={{ borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`, pb: 1, mb: 2, ...TYPOGRAPHY.H4, fontFamily: FONT_FAMILY, color: COLORS.TEXT }}>
                <Box flex={2}>Type of Equipment</Box>
                <Box width="120px" textAlign="center">Quantity</Box>
                <Box width="140px" textAlign="center">Working Hrs</Box>
                <Box flex={1.2}>Purpose</Box>
                <Box width="120px" textAlign="center">Actions</Box>
              </Box>

              {/* Table Rows - With Edit and Delete Icons */}
              {wmrData.machineryEquipment.map((machine, index) => (
                <Box key={index} display="flex" gap={2} mb={2} alignItems="center">
                  <Box flex={2} sx={{ ...machineCellStyle, height: "44px", display: "flex", alignItems: "center" }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={machine.equipment}
                      onChange={(e) => handleMachineChange(index, 'equipment', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: '44px',
                          '& fieldset': { border: 'none' },
                        },
                      }}
                    />
                  </Box>
                  <Box width="120px" sx={{ ...machineCellStyle, height: "44px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <TextField
                      size="small"
                      placeholder="0"
                      value={machine.quantity}
                      onChange={(e) => handleNumericInput(e, (val) => handleMachineChange(index, 'quantity', val))}
                      onCut={(e) => handleCutOperation(e, () => handleMachineChange(index, 'quantity', ''))}
                      sx={{
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                          height: '44px',
                          '& fieldset': { border: 'none' },
                        },
                      }}
                      inputProps={{
                        style: { textAlign: 'center' },
                        inputMode: 'numeric',
                        pattern: '[0-9]*'
                      }}
                    />
                  </Box>
                  <Box width="140px" sx={{ ...machineCellStyle, height: "44px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <TextField
                      size="small"
                      placeholder="0"
                      value={machine.hours}
                      onChange={(e) => handleNumericInput(e, (val) => handleMachineChange(index, 'hours', val))}
                      onCut={(e) => handleCutOperation(e, () => handleMachineChange(index, 'hours', ''))}
                      sx={{
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                          height: '44px',
                          '& fieldset': { border: 'none' },
                        },
                      }}
                      inputProps={{
                        style: { textAlign: 'center' },
                        inputMode: 'numeric',
                        pattern: '[0-9]*'
                      }}
                    />
                  </Box>
                  <Box flex={1.2} sx={{ ...machineCellStyle, height: "44px", display: "flex", alignItems: "center" }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="-"
                      value={machine.utilization || ""}
                      onChange={(e) => handleMachineChange(index, 'utilization', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: '44px',
                          '& fieldset': { border: 'none' },
                        },
                      }}
                    />
                  </Box>
                  <Box width="120px" sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setNewMachine({
                          equipment: machine.equipment,
                          quantity: machine.quantity,
                          hours: machine.hours,
                          utilization: machine.utilization
                        });
                        setShowMachineEntry(true);
                        setEditingEntry({
                          section: 'machine',
                          index: index,
                          data: machine
                        });
                      }}
                      sx={{ color: COLORS.PRIMARY }}
                    >
                      <Icon name="Action3" size={16} color="#8CD47E" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteMachine(index)}
                      sx={{ color: COLORS.ERROR }}
                    >
                      <Icon name="close2" size={16} />
                    </IconButton>
                  </Box>
                </Box>
              ))}

              {/* Add/Edit Entry Form */}
              {showMachineEntry && (
                <Box mt={3} sx={{ border: `1px solid ${COLORS.LIGHT_SECONDARY}`, borderRadius: "8px", p: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography sx={labelStyle}>
                      {editingEntry.section === 'machine' ? "Edit Machine Entry" : "Add New Machine Entry"}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setShowMachineEntry(false);
                        setNewMachine({ equipment: "", quantity: "", hours: "", utilization: "" });
                        setEditingEntry({
                          section: null,
                          index: null,
                          data: null
                        });
                      }}
                      sx={{ color: COLORS.ERROR }}
                    >
                      <Icon name="close2" />
                    </IconButton>
                  </Box>
                  <Box display="flex" gap={2} mb={2}>
                    <Box flex={2}>
                      <Typography sx={labelStyle}>Type of Equipment</Typography>
                      <TextField fullWidth size="small" placeholder="-" value={newMachine.equipment} onChange={(e) => setNewMachine({ ...newMachine, equipment: e.target.value })} sx={inputStyle} />
                    </Box>
                    <Box width="120px">
                      <Typography sx={labelStyle}>Quantity</Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="0"
                        value={newMachine.quantity}
                        onChange={(e) => handleNumericInput(e, (val) => setNewMachine({ ...newMachine, quantity: val }))}
                        onCut={(e) => handleCutOperation(e, () => setNewMachine({ ...newMachine, quantity: '' }))}
                        sx={inputStyle}
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      />
                    </Box>
                    <Box width="140px">
                      <Typography sx={labelStyle}>Working Hrs</Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="0"
                        value={newMachine.hours}
                        onChange={(e) => handleNumericInput(e, (val) => setNewMachine({ ...newMachine, hours: val }))}
                        onCut={(e) => handleCutOperation(e, () => setNewMachine({ ...newMachine, hours: '' }))}
                        sx={inputStyle}
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      />
                    </Box>
                    <Box flex={1.2}>
                      <Typography sx={labelStyle}>Purpose</Typography>
                      <TextField fullWidth size="small" placeholder="-" value={newMachine.utilization || ""} onChange={(e) => setNewMachine({ ...newMachine, utilization: e.target.value })} sx={inputStyle} />
                    </Box>
                  </Box>
                  <Box display="flex" justifyContent="flex-end" mt={1.5}>
                    <Button
                      variant="outlined"
                      startIcon={<Icon name="plus2" />}
                      sx={{
                        ...addBtnStyle,
                        backgroundColor: editingEntry.section === 'machine' ? COLORS.PRIMARY : COLORS.SECONDARY,
                      }}
                      onClick={handleAddMachine}
                    >
                      {editingEntry.section === 'machine' ? "Update Entry" : "Add Entry"}
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Add Button */}
              {!showMachineEntry && (
                <Box display="flex" justifyContent="flex-end" mt={1.5}>
                  <Button variant="outlined" startIcon={<Icon name="plus2" />} sx={addBtnStyle} onClick={() => {
                    setNewMachine({ equipment: "", quantity: "", hours: "", utilization: "" });
                    setShowMachineEntry(true);
                    setEditingEntry({
                      section: null,
                      index: null,
                      data: null
                    });
                  }}>
                    Add New Entry
                  </Button>
                </Box>
              )}
            </Box>

            {/* ================= 7. MATERIAL STATUS ================= */}
            <Box mt={4}>
              <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.PRIMARY, mb: 1.5, fontFamily: FONT_FAMILY }}>
                7. Material Status:
              </Typography>

              {/* Table Header - Updated to match alignment */}
              <Box display="flex" gap={2} sx={{ borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`, py: 1, ...TYPOGRAPHY.H4, color: COLORS.TEXT, fontFamily: FONT_FAMILY, mb: 2 }}>
                <Box flex={2}>Material Description</Box>
                <Box width="140px" textAlign="center">Material Received</Box>
                <Box width="140px" textAlign="center">Material Consumed</Box>
                <Box width="140px" textAlign="center">Balance At Site</Box>
                <Box width="120px" textAlign="center">Actions</Box>
              </Box>

              {/* Table Rows - With Edit and Delete Icons */}
              {wmrData.materialStatus.map((material, index) => (
                <Box key={index} display="flex" gap={2} mb={2} alignItems="center">
                  <Box flex={2} sx={{ ...machineCellStyle, height: "44px", display: "flex", alignItems: "center" }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={material.description}
                      onChange={(e) => handleMaterialChange(index, 'description', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: '44px',
                          '& fieldset': { border: 'none' },
                        },
                      }}
                    />
                  </Box>
                  <Box width="140px" sx={{ ...machineCellStyle, height: "44px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <TextField
                      size="small"
                      value={material.received}
                      onChange={(e) => handleNumericInput(e, (val) => handleMaterialChange(index, 'received', val))}
                      onCut={(e) => handleCutOperation(e, () => handleMaterialChange(index, 'received', ''))}
                      sx={{
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                          height: '44px',
                          '& fieldset': { border: 'none' },
                        },
                      }}
                      inputProps={{
                        style: { textAlign: 'center' },
                        inputMode: 'numeric',
                        pattern: '[0-9]*'
                      }}
                    />
                  </Box>
                  <Box width="140px" sx={{ ...machineCellStyle, height: "44px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <TextField
                      size="small"
                      value={material.consumed}
                      onChange={(e) => handleNumericInput(e, (val) => handleMaterialChange(index, 'consumed', val))}
                      onCut={(e) => handleCutOperation(e, () => handleMaterialChange(index, 'consumed', ''))}
                      sx={{
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                          height: '44px',
                          '& fieldset': { border: 'none' },
                        },
                      }}
                      inputProps={{
                        style: { textAlign: 'center' },
                        inputMode: 'numeric',
                        pattern: '[0-9]*'
                      }}
                    />
                  </Box>
                  <Box width="140px" sx={{ ...machineCellStyle, height: "44px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, textAlign: 'center' }}>
                      {material.balance}
                    </Typography>
                  </Box>
                  <Box width="120px" sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setNewMaterial({
                          description: material.description,
                          received: material.received,
                          consumed: material.consumed,
                          balance: material.balance
                        });
                        setShowMaterialEntry(true);
                        setEditingEntry({
                          section: 'material',
                          index: index,
                          data: material
                        });
                      }}
                      sx={{ color: COLORS.PRIMARY }}
                    >
                      <Icon name="Action3" size={16} color="#8CD47E" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteMaterial(index)}
                      sx={{ color: COLORS.ERROR }}
                    >
                      <Icon name="close2" size={16} />
                    </IconButton>
                  </Box>
                </Box>
              ))}

              {/* Add/Edit Entry Form */}
              {showMaterialEntry && (
                <Box mt={3} sx={{ border: `1px solid ${COLORS.LIGHT_SECONDARY}`, borderRadius: "8px", p: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography sx={labelStyle}>
                      {editingEntry.section === 'material' ? "Edit Material Entry" : "Add New Material Entry"}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setShowMaterialEntry(false);
                        setNewMaterial({ description: "", received: "", consumed: "", balance: "" });
                        setEditingEntry({
                          section: null,
                          index: null,
                          data: null
                        });
                      }}
                      sx={{ color: COLORS.ERROR }}
                    >
                      <Icon name="close2" />
                    </IconButton>
                  </Box>
                  <Box display="flex" gap={2} mb={2} alignItems="flex-end">
                    <Box flex={2}>
                      <Typography sx={labelStyle}>Material Description</Typography>
                      <TextField fullWidth placeholder="-" size="small" value={newMaterial.description} onChange={(e) => setNewMaterial({ ...newMaterial, description: e.target.value })} sx={inputStyle} />
                    </Box>
                    <Box width="140px">
                      <Typography sx={{ ...labelStyle, whiteSpace: "nowrap" }}>Material Received</Typography>
                      <TextField
                        fullWidth
                        placeholder="0"
                        size="small"
                        value={newMaterial.received}
                        onChange={(e) => handleNumericInput(e, (val) => setNewMaterial({ ...newMaterial, received: val }))}
                        onCut={(e) => handleCutOperation(e, () => setNewMaterial({ ...newMaterial, received: '' }))}
                        sx={inputStyle}
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      />
                    </Box>
                    <Box width="140px">
                      <Typography sx={{ ...labelStyle, whiteSpace: "nowrap" }}>Material Consumed</Typography>
                      <TextField
                        fullWidth
                        placeholder="0"
                        size="small"
                        value={newMaterial.consumed}
                        onChange={(e) => handleNumericInput(e, (val) => setNewMaterial({ ...newMaterial, consumed: val }))}
                        onCut={(e) => handleCutOperation(e, () => setNewMaterial({ ...newMaterial, consumed: '' }))}
                        sx={inputStyle}
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      />
                    </Box>
                    <Box width="140px">
                      <Typography sx={{ ...labelStyle, whiteSpace: "nowrap" }}>Balance At Site</Typography>
                      <TextField
                        fullWidth
                        placeholder="0"
                        size="small"
                        value={newMaterial.balance}
                        disabled
                        sx={{
                          ...inputStyle,
                          '& .MuiInputBase-input.Mui-disabled': {
                            WebkitTextFillColor: COLORS.SECONDARY,
                            textAlign: 'center',
                          }
                        }}
                      />
                    </Box>
                  </Box>
                  <Box display="flex" justifyContent="flex-end" mt={1.5}>
                    <Button
                      variant="outlined"
                      startIcon={<Icon name="plus2" />}
                      sx={{
                        ...addBtnStyle,
                        backgroundColor: editingEntry.section === 'material' ? COLORS.PRIMARY : COLORS.SECONDARY,
                      }}
                      onClick={handleAddMaterial}
                    >
                      {editingEntry.section === 'material' ? "Update Entry" : "Add Entry"}
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Add Button */}
              {!showMaterialEntry && (
                <Box display="flex" justifyContent="flex-end" mt={1.5}>
                  <Button variant="outlined" startIcon={<Icon name="plus2" />} sx={addBtnStyle} onClick={() => {
                    setNewMaterial({ description: "", received: "", consumed: "", balance: "" });
                    setShowMaterialEntry(true);
                    setEditingEntry({
                      section: null,
                      index: null,
                      data: null
                    });
                  }}>
                    Add New Entry
                  </Button>
                </Box>
              )}
            </Box>

            {/* ================= 8. QUALITY CONTROL ACTIVITIES ================= */}
            <Box mt={4}>
              <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.PRIMARY, mb: 1.5, fontFamily: FONT_FAMILY }}>
                8. Quality Control Activities:
              </Typography>

              {/* Table Header */}
              <Box display="flex" alignItems="center" sx={{ borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`, py: 1, mb: 2, ...TYPOGRAPHY.H4, color: COLORS.TEXT, fontFamily: FONT_FAMILY }}>
                <Box flex={2.2} pl={2}>Inspection Carried Out</Box>
                <Box width="140px" textAlign="center">Test Conducted</Box>
                <Box flex={1.8} pl={2}>NCR / Observations</Box>
                <Box width="120px" textAlign="center">Status</Box>
                <Box width="120px" textAlign="center">Actions</Box>
              </Box>

              {/* Table Rows - With Edit and Delete Icons */}
              {wmrData.qualityControl.map((row, index) => (
                <Box key={index} display="flex" gap={2} mb={2} alignItems="center">
                  <Box flex={2.2} sx={{ ...machineCellStyle, height: "44px", display: "flex", alignItems: "center" }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="-"
                      value={row.inspection}
                      onChange={(e) => handleQualityChange(index, 'inspection', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: '44px',
                          '& fieldset': { border: 'none' },
                        },
                      }}
                    />
                  </Box>
                  <Box width="140px" sx={{ ...machineCellStyle, height: "44px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <TextField
                      size="small"
                      placeholder="0"
                      value={row.test}
                      onChange={(e) => handleNumericInput(e, (val) => handleQualityChange(index, 'test', val))}
                      onCut={(e) => handleCutOperation(e, () => handleQualityChange(index, 'test', ''))}
                      sx={{
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                          height: '44px',
                          '& fieldset': { border: 'none' },
                        },
                      }}
                      inputProps={{
                        style: { textAlign: 'center' },
                        inputMode: 'numeric',
                        pattern: '[0-9]*'
                      }}
                    />
                  </Box>
                  <Box flex={1.8} sx={{ ...machineCellStyle, height: "44px", display: "flex", alignItems: "center" }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="-"
                      value={row.observation}
                      onChange={(e) => handleQualityChange(index, 'observation', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: '44px',
                          '& fieldset': { border: 'none' },
                        },
                      }}
                    />
                  </Box>
                  <Box width="120px" sx={{ ...machineCellStyle, height: "44px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <TextField
                      select
                      size="small"
                      value={row.status || "OPEN"}
                      onChange={(e) => handleQualityChange(index, 'status', e.target.value)}
                      sx={{
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                          height: '44px',
                          '& fieldset': { border: 'none' },
                        },
                      }}
                      SelectProps={{
                        MenuProps: {
                          PaperProps: {
                            sx: {
                              maxHeight: 200,
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem value="OPEN">Open</MenuItem>
                      <MenuItem value="CLOSED">Closed</MenuItem>
                    </TextField>
                  </Box>
                  <Box width="120px" sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setNewQuality({
                          inspection: row.inspection,
                          test: row.test,
                          observation: row.observation,
                          status: row.status || "OPEN"
                        });
                        setShowQualityEntry(true);
                        setEditingEntry({
                          section: 'quality',
                          index: index,
                          data: row
                        });
                      }}
                      sx={{ color: COLORS.PRIMARY }}
                    >
                      <Icon name="Action3" size={16} color="#8CD47E" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteQuality(index)}
                      sx={{ color: COLORS.ERROR }}
                    >
                      <Icon name="close2" size={16} />
                    </IconButton>
                  </Box>
                </Box>
              ))}

              {/* Add/Edit Entry Form */}
              {showQualityEntry && (
                <Box mt={3} sx={{ border: `1px solid ${COLORS.LIGHT_SECONDARY}`, borderRadius: "8px", p: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography sx={labelStyle}>
                      {editingEntry.section === 'quality' ? "Edit Quality Control Entry" : "Add New Quality Control Entry"}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setShowQualityEntry(false);
                        setNewQuality({ inspection: "", test: "", observation: "", status: "OPEN" });
                        setEditingEntry({
                          section: null,
                          index: null,
                          data: null
                        });
                      }}
                      sx={{ color: COLORS.ERROR }}
                    >
                      <Icon name="close2" />
                    </IconButton>
                  </Box>
                  <Box display="flex" gap={2} mb={2}>
                    <Box flex={2.2}>
                      <Typography sx={labelStyle}>Inspection Carried Out</Typography>
                      <TextField fullWidth size="small" placeholder="-" value={newQuality.inspection} onChange={(e) => setNewQuality({ ...newQuality, inspection: e.target.value })} sx={inputStyle} />
                    </Box>
                    <Box width="140px">
                      <Typography sx={labelStyle}>Test Conducted</Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="0"
                        value={newQuality.test}
                        onChange={(e) => handleNumericInput(e, (val) => setNewQuality({ ...newQuality, test: val }))}
                        onCut={(e) => handleCutOperation(e, () => setNewQuality({ ...newQuality, test: '' }))}
                        sx={inputStyle}
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      />
                    </Box>
                    <Box flex={1.8}>
                      <Typography sx={labelStyle}>NCR / Observations</Typography>
                      <TextField fullWidth size="small" placeholder="-" value={newQuality.observation} onChange={(e) => setNewQuality({ ...newQuality, observation: e.target.value })} sx={inputStyle} />
                    </Box>
                    <Box width="120px">
                      <Typography sx={labelStyle}>Status</Typography>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={newQuality.status || "OPEN"}
                        onChange={(e) => setNewQuality({ ...newQuality, status: e.target.value })}
                        sx={inputStyle}
                        SelectProps={{
                          MenuProps: {
                            PaperProps: {
                              sx: {
                                maxHeight: 200,
                              },
                            },
                          },
                        }}
                      >
                        <MenuItem value="OPEN">Open</MenuItem>
                        <MenuItem value="CLOSED">Closed</MenuItem>
                      </TextField>
                    </Box>
                  </Box>
                  <Box display="flex" justifyContent="flex-end" mt={1.5}>
                    <Button
                      variant="outlined"
                      startIcon={<Icon name="plus2" />}
                      sx={{
                        ...addBtnStyle,
                        backgroundColor: editingEntry.section === 'quality' ? COLORS.PRIMARY : COLORS.SECONDARY,
                      }}
                      onClick={handleAddQuality}
                    >
                      {editingEntry.section === 'quality' ? "Update Entry" : "Add Entry"}
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Add Button */}
              {!showQualityEntry && (
                <Box display="flex" justifyContent="flex-end" mt={1.5}>
                  <Button variant="outlined" startIcon={<Icon name="plus2" />} sx={addBtnStyle} onClick={() => {
                    setNewQuality({ inspection: "", test: "", observation: "", status: "OPEN" });
                    setShowQualityEntry(true);
                    setEditingEntry({
                      section: null,
                      index: null,
                      data: null
                    });
                  }}>
                    Add New Entry
                  </Button>
                </Box>
              )}
            </Box>

            {/* ================= 9. SAFETY & HSE STATUS ================= */}
            <Box mt={4}>
              <Typography sx={{ ...TYPOGRAPHY.H3, fontFamily: FONT_FAMILY, color: COLORS.PRIMARY, mb: 2 }}>
                9. Safety & HSE Status:
              </Typography>

              {/* Safety Compliance */}
              <Box mb={2.5}>
                <Typography sx={{ ...TYPOGRAPHY.H4, mb: 0.8, fontFamily: FONT_FAMILY, color: COLORS.PRIMARY }}>
                  Safety Compliance
                </Typography>
                <RichTextEditor
                  value={wmrData.safetyHSE.safetyCompliance}
                  onChange={(value) => setWmrData(prev => ({
                    ...prev,
                    safetyHSE: { ...prev.safetyHSE, safetyCompliance: value }
                  }))}
                  placeholder="Describe safety compliance activities..."
                />
              </Box>

              {/* Toolbox Talks */}
              <Box mb={2.5}>
                <Typography sx={{ ...TYPOGRAPHY.H4, mb: 0.8, fontFamily: FONT_FAMILY, color: COLORS.PRIMARY }}>
                  Toolbox Talks
                </Typography>
                <RichTextEditor
                  value={wmrData.safetyHSE.toolboxTalks}
                  onChange={(value) => setWmrData(prev => ({
                    ...prev,
                    safetyHSE: { ...prev.safetyHSE, toolboxTalks: value }
                  }))}
                  placeholder="Describe toolbox talks conducted..."
                />
              </Box>

              {/* Accidents / Near Misses */}
              <Box mb={2.5}>
                <Typography sx={{ ...TYPOGRAPHY.H4, mb: 0.8, fontFamily: FONT_FAMILY, color: COLORS.PRIMARY }}>
                  Accidents / Near Misses
                </Typography>
                <RichTextEditor
                  value={wmrData.safetyHSE.accidentsNearMisses}
                  onChange={(value) => setWmrData(prev => ({
                    ...prev,
                    safetyHSE: { ...prev.safetyHSE, accidentsNearMisses: value }
                  }))}
                  placeholder="Report any accidents or near misses..."
                />
              </Box>
            </Box>

            {/* ================= 10. ISSUES / CONSTRAINTS ================= */}
            <Box mt={4}>
              <Typography sx={{ ...TYPOGRAPHY.H3, fontFamily: FONT_FAMILY, color: COLORS.PRIMARY, mb: 2 }}>
                10. Issues / Constraints:
              </Typography>

              {/* Technical */}
              <Box mb={2.5}>
                <Typography sx={{ ...TYPOGRAPHY.H4, mb: 0.8, fontFamily: FONT_FAMILY, color: COLORS.PRIMARY }}>
                  Technical
                </Typography>
                <RichTextEditor
                  value={wmrData.issuesConstraints.technical}
                  onChange={(value) => setWmrData(prev => ({
                    ...prev,
                    issuesConstraints: { ...prev.issuesConstraints, technical: value }
                  }))}
                  placeholder="Describe technical issues/constraints..."
                />
              </Box>

              {/* Material */}
              <Box mb={2.5}>
                <Typography sx={{ ...TYPOGRAPHY.H4, mb: 0.8, fontFamily: FONT_FAMILY, color: COLORS.PRIMARY }}>
                  Material
                </Typography>
                <RichTextEditor
                  value={wmrData.issuesConstraints.material}
                  onChange={(value) => setWmrData(prev => ({
                    ...prev,
                    issuesConstraints: { ...prev.issuesConstraints, material: value }
                  }))}
                  placeholder="Describe material issues/constraints..."
                />
              </Box>

              {/* Manpower */}
              <Box mb={2.5}>
                <Typography sx={{ ...TYPOGRAPHY.H4, mb: 0.8, fontFamily: FONT_FAMILY, color: COLORS.PRIMARY }}>
                  Manpower
                </Typography>
                <RichTextEditor
                  value={wmrData.issuesConstraints.manpower}
                  onChange={(value) => setWmrData(prev => ({
                    ...prev,
                    issuesConstraints: { ...prev.issuesConstraints, manpower: value }
                  }))}
                  placeholder="Describe manpower issues/constraints..."
                />
              </Box>

              {/* Design/Approval */}
              <Box mb={2.5}>
                <Typography sx={{ ...TYPOGRAPHY.H4, mb: 0.8, fontFamily: FONT_FAMILY, color: COLORS.PRIMARY }}>
                  Design/Approval
                </Typography>
                <RichTextEditor
                  value={wmrData.issuesConstraints.designApproval}
                  onChange={(value) => setWmrData(prev => ({
                    ...prev,
                    issuesConstraints: { ...prev.issuesConstraints, designApproval: value }
                  }))}
                  placeholder="Describe design/approval issues/constraints..."
                />
              </Box>
            </Box>

            {/* ================= 11. INSTRUCTIONS / DECISIONS FROM VISITOR ================= */}
            <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.PRIMARY, mt: 4, mb: 1.5, fontFamily: FONT_FAMILY }}>
              11. Instructions / Decisions from Visitor:
            </Typography>

            {/* Visitor Instructions Table */}
            <Box mb={3}>
              {/* Table Header */}
              <Box
                display="flex"
                sx={{
                  borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                  py: 0.8,
                }}
              >
                <Box width="120px" sx={{ ...TYPOGRAPHY.H4, fontFamily: FONT_FAMILY, color: COLORS.TEXT }}>
                  Date
                </Box>
                <Box flex={1.5} sx={{ ...TYPOGRAPHY.H4, fontFamily: FONT_FAMILY, color: COLORS.TEXT }}>
                  Visitor Name
                </Box>
                <Box flex={1.2} sx={{ ...TYPOGRAPHY.H4, fontFamily: FONT_FAMILY, color: COLORS.TEXT }}>
                  Designation
                </Box>
                <Box flex={2} sx={{ ...TYPOGRAPHY.H4, fontFamily: FONT_FAMILY, color: COLORS.TEXT }}>
                  Instruction / Decision
                </Box>
                <Box flex={1.5} sx={{ ...TYPOGRAPHY.H4, fontFamily: FONT_FAMILY, color: COLORS.TEXT }}>
                  Remark
                </Box>
                <Box width="120px" sx={{ ...TYPOGRAPHY.H4, fontFamily: FONT_FAMILY, color: COLORS.TEXT, textAlign: "center" }}>
                  Actions
                </Box>
              </Box>

              {/* Table Body */}
              {wmrData.instructionsDecisions.map((row, i) => (
                <Box
                  key={i}
                  display="flex"
                  sx={{
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    py: 0.8,
                    alignItems: "flex-start"
                  }}
                >
                  <Box width="120px" sx={{ ...TYPOGRAPHY.BODY_LARGE, fontFamily: FONT_FAMILY, color: COLORS.SECONDARY, display: "flex", alignItems: "center" }}>
                    <Typography sx={{ fontSize: "14px", wordWrap: "break-word" }}>
                      {row.date ? format(row.date, "dd/MM/yyyy") : ""}
                    </Typography>
                  </Box>
                  <Box flex={1.5} sx={{ ...TYPOGRAPHY.BODY_LARGE, fontFamily: FONT_FAMILY, color: COLORS.SECONDARY, display: "flex", alignItems: "center" }}>
                    <Typography sx={{ fontSize: "14px", wordWrap: "break-word", lineHeight: 1.4 }}>
                      {row.visitorName || "-"}
                    </Typography>
                  </Box>
                  <Box flex={1.2} sx={{ ...TYPOGRAPHY.BODY_LARGE, fontFamily: FONT_FAMILY, color: COLORS.SECONDARY, display: "flex", alignItems: "center" }}>
                    <Typography sx={{ fontSize: "14px", wordWrap: "break-word", lineHeight: 1.4 }}>
                      {row.designation || "-"}
                    </Typography>
                  </Box>
                  <Box flex={2} sx={{ ...TYPOGRAPHY.BODY_LARGE, fontFamily: FONT_FAMILY, color: COLORS.SECONDARY, display: "flex", alignItems: "center" }}>
                    <Typography sx={{ fontSize: "14px", wordWrap: "break-word", lineHeight: 1.4, whiteSpace: "pre-wrap" }}>
                      {row.instruction || "-"}
                    </Typography>
                  </Box>
                  <Box flex={1.5} sx={{ ...TYPOGRAPHY.BODY_LARGE, fontFamily: FONT_FAMILY, color: COLORS.SECONDARY, display: "flex", alignItems: "center" }}>
                    <Typography sx={{ fontSize: "14px", wordWrap: "break-word", lineHeight: 1.4, whiteSpace: "pre-wrap" }}>
                      {row.remark || "-"}
                    </Typography>
                  </Box>
                  <Box width="120px" sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditVisitor(i)}
                      sx={{ color: COLORS.PRIMARY }}
                    >
                      <Icon name="Action3" size={16} color="#8CD47E" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteVisitor(i)}
                      sx={{ color: COLORS.ERROR }}
                    >
                      <Icon name="close2" size={16} />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Add New Entry Button */}
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button
                variant="outlined"
                startIcon={<Icon name="plus2" />}
                sx={addBtnStyle}
                onClick={() => setShowVisitorEntry(true)}
              >
                Add New Entry
              </Button>
            </Box>

            {/* Input Form */}
            {showVisitorEntry && (
              <Box py={2} sx={{ border: `1px solid ${COLORS.LIGHT_SECONDARY}`, borderRadius: "8px", p: 2, mt: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography sx={{ fontFamily: FONT_FAMILY, color: COLORS.PRIMARY, ...TYPOGRAPHY.H4 }}>
                    {editingVisitor !== null ? "Edit Visitor Entry" : "Add New Visitor Entry"}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setShowVisitorEntry(false);
                      setEditingVisitor(null);
                      setShowOtherConsultantInput(false);
                      setNewVisitor({
                        date: new Date(),
                        visitorName: "",
                        designation: "",
                        otherDesignation: "",
                        instruction: "",
                        remark: ""
                      });
                    }}
                    sx={{ color: COLORS.ERROR }}
                  >
                    <Icon name="close2" />
                  </IconButton>
                </Box>

                {/* First Row - Date, Visitor Name, Designation */}
                <Box display="flex" gap={2} mb={2}>
                  <Box width="150px">
                    <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 0.5, fontFamily: FONT_FAMILY }}>
                      Date
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      value={format(newVisitor.date, "yyyy-MM-dd")}
                      onChange={(e) => setNewVisitor({ ...newVisitor, date: new Date(e.target.value) })}
                      sx={inputStyle}
                    />
                  </Box>
                  <Box flex={1}>
                    <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 0.5, fontFamily: FONT_FAMILY }}>
                      Visitor Name
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter visitor name"
                      size="small"
                      value={newVisitor.visitorName}
                      onChange={(e) => setNewVisitor({ ...newVisitor, visitorName: e.target.value })}
                      sx={inputStyle}
                    />
                  </Box>
                  <Box width="200px">
                    <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 0.5, fontFamily: FONT_FAMILY }}>
                      Designation
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={newVisitor.designation}
                      onChange={(e) => handleDesignationChange(e.target.value)}
                      sx={inputStyle}
                      SelectProps={{
                        MenuProps: {
                          PaperProps: {
                            sx: {
                              maxHeight: 200,
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem value="Structural Consultant">Structural Consultant</MenuItem>
                      <MenuItem value="MEP Consultant">MEP Consultant</MenuItem>
                      <MenuItem value="Architect">Architect</MenuItem>
                      <MenuItem value="Interior Designer">Interior Designer</MenuItem>
                      <MenuItem value="Developer / Builder">Developer / Builder</MenuItem>
                      <MenuItem value="Individual">Individual</MenuItem>
                      <MenuItem value="Other Consultant">Other Consultant</MenuItem>
                    </TextField>
                  </Box>
                </Box>

                {/* Second Row - Other Consultant (conditional) */}
                {showOtherConsultantInput && (
                  <Box display="flex" gap={2} mb={2}>
                    <Box flex={1}>
                      <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 0.5, fontFamily: FONT_FAMILY }}>
                        Specify Other Consultant
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Enter consultant designation"
                        size="small"
                        value={newVisitor.otherDesignation}
                        onChange={(e) => setNewVisitor({ ...newVisitor, otherDesignation: e.target.value })}
                        sx={inputStyle}
                      />
                    </Box>
                  </Box>
                )}

                {/* Third Row - Instruction / Decision */}
                <Box display="flex" gap={2} mb={2}>
                  <Box flex={1}>
                    <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 0.5, fontFamily: FONT_FAMILY }}>
                      Instruction / Decision
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter instruction or decision"
                      size="small"
                      value={newVisitor.instruction}
                      onChange={(e) => setNewVisitor({ ...newVisitor, instruction: e.target.value })}
                      multiline
                      maxRows={3}
                      sx={inputStyle}
                    />
                  </Box>
                </Box>

                {/* Fourth Row - Remark */}
                <Box display="flex" gap={1} mb={2}>
                  <Box flex={1}>
                    <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 0.5, fontFamily: FONT_FAMILY }}>
                      Remark
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter remark"
                      size="small"
                      value={newVisitor.remark}
                      onChange={(e) => setNewVisitor({ ...newVisitor, remark: e.target.value })}
                      multiline
                      maxRows={3}
                      sx={inputStyle}
                    />
                  </Box>
                </Box>

                <Box display="flex" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    startIcon={<Icon name="plus2" />}
                    sx={addBtnStyle}
                    onClick={handleAddVisitor}
                  >
                    {editingVisitor !== null ? "Update Entry" : "Add Entry"}
                  </Button>
                </Box>
              </Box>
            )}

            {/* ================= 12. REPORTS ================= */}
            <Box mt={4}>
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ cursor: "pointer" }} onClick={() => setOpenReports(!openReports)}>
                <Typography sx={{ ...TYPOGRAPHY.H3, fontFamily: FONT_FAMILY, color: COLORS.PRIMARY }}>
                  12. Reports
                </Typography>
                <Icon name={openReports ? "chevronUp" : "chevronDown"} />
              </Box>

              {openReports && (
                <Box mt={2}>
                  <Box mb={2}>
                    <Button component="label" variant="outlined" startIcon={<Icon name="plus2" />} sx={addBtnStyle}>
                      Upload Reports
                      <input type="file" hidden multiple onChange={handleReportUpload} />
                    </Button>
                  </Box>

                  {uploadedReports.length > 0 && (
                    <Box>
                      {uploadedReports.map((file, index) => (
                        <Box key={index} display="flex" alignItems="center" justifyContent="space-between" sx={{ border: `1px solid ${COLORS.LIGHT_SECONDARY}`, borderRadius: "8px", px: 2, py: 1, mb: 1 }}>
                          <Typography sx={{ fontFamily: FONT_FAMILY, color: COLORS.PRIMARY }}>
                            {file.name}
                          </Typography>
                          <Box display="flex" gap={1}>
                            <IconButton onClick={() => {
                              if (file instanceof File) {
                                // Newly uploaded file - use createObjectURL
                                window.open(URL.createObjectURL(file));
                              } else if (file.path) {
                                // File from database - open via server URL
                                window.open(`${url}${file.path.replace(/\\/g, '/')}`);
                              }
                            }}>
                              <Icon name="Action1" />
                            </IconButton>
                            <IconButton onClick={() => handleRemoveReport(index)}>
                              <Icon name="close2" color={COLORS.ERROR} />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            {/* ================= 13. DECISION & DISCUSSION RECORDS ================= */}
            <Box mt={4}>
              <Typography sx={{ ...TYPOGRAPHY.H3, fontFamily: FONT_FAMILY, color: COLORS.PRIMARY, mb: 2 }}>
                13. Decision & Discussion Records:
              </Typography>
              <RichTextEditor
                value={wmrData.decisionDiscussion}
                onChange={(value) => setWmrData(prev => ({ ...prev, decisionDiscussion: value }))}
                placeholder="Record decisions and discussions..."
              />
            </Box>

            {/* ================= 14. PLAN FOR NEXT WEEK ================= */}
            <Box mt={4}>
              <Typography sx={{ ...TYPOGRAPHY.H3, fontFamily: FONT_FAMILY, color: COLORS.PRIMARY, mb: 2 }}>
                14. Plan for Next Week
              </Typography>

              {/* Target Activities */}
              <Box mb={2.5}>
                <Typography sx={{ ...TYPOGRAPHY.H4, mb: 0.8, fontFamily: FONT_FAMILY, color: COLORS.PRIMARY }}>
                  Target Activities
                </Typography>
                <RichTextEditor
                  value={wmrData.planNextWeek.targetActivities}
                  onChange={(value) => setWmrData(prev => ({
                    ...prev,
                    planNextWeek: { ...prev.planNextWeek, targetActivities: value }
                  }))}
                  placeholder="Describe target activities for next week..."
                />
              </Box>

              {/* Resource Planning */}
              <Box mb={2.5}>
                <Typography sx={{ ...TYPOGRAPHY.H4, mb: 0.8, fontFamily: FONT_FAMILY, color: COLORS.PRIMARY }}>
                  Resource Planning
                </Typography>
                <RichTextEditor
                  value={wmrData.planNextWeek.resourcePlanning}
                  onChange={(value) => setWmrData(prev => ({
                    ...prev,
                    planNextWeek: { ...prev.planNextWeek, resourcePlanning: value }
                  }))}
                  placeholder="Describe resource planning for next week..."
                />
              </Box>
            </Box>

            {/* ================= 15. PHOTOGRAPHIC PROGRESS ================= */}
            <Box mt={4}>
              <Typography sx={{ ...TYPOGRAPHY.H3, fontFamily: FONT_FAMILY, color: COLORS.PRIMARY, mb: 2 }}>
                15. Photographic Progress
              </Typography>

              {/* Add Photo Button */}
              <Box mb={3}>
                <Button
                  variant="contained"
                  startIcon={<Icon name="plus2" />}
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontFamily: FONT_FAMILY,
                    backgroundColor: COLORS.SECONDARY,
                    color: "#fff",
                    px: 3,
                    py: 1.2,
                    "&:hover": {
                      backgroundColor: COLORS.SECONDARY,
                    }
                  }}
                  onClick={() => setShowPhotoUploadDialog(true)}
                  disabled={uploadedPhotos.length >= 4}
                >
                  Add Photo 
                </Button>
              </Box>
              <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
                {uploadedPhotos.map((photo, index) => (
                  <Box key={index} sx={{ position: "relative" }}>
                    {/* Photo Display */}
                    <img
                      src={photo.imageUrl || URL.createObjectURL(photo.file)}
                      alt="progress"
                      style={{
                        width: "100%",
                        height: "auto",
                        maxHeight: "300px",
                        objectFit: "contain"
                      }}
                    />

                    {/* Close Button at Top Right */}
                    <IconButton
                      size="small"
                      onClick={() => removePhoto(index)}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        backgroundColor: "#fff",
                        color: COLORS.ERROR,
                        "&:hover": { backgroundColor: "#ffebee" }
                      }}
                    >
                      <Icon name="close2" />
                    </IconButton>

                    {/* Photo Info */}
                    <Box sx={{ p: 1 }}>
                      <Typography
                        sx={{
                          ...TYPOGRAPHY.BODY_SMALL,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.TEXT,
                          fontWeight: "medium",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical"
                        }}
                      >
                        {photo.title || photo.name}
                      </Typography>


                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Empty State */}
              {uploadedPhotos.length === 0 && (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 8,
                    border: `2px dashed ${COLORS.LIGHT_SECONDARY}`,
                    borderRadius: "12px",
                    backgroundColor: "#fafafa"
                  }}
                >
                  <Icon name="image" size={48} color={COLORS.LIGHT_SECONDARY} />
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.BODY_STANDARD,
                      color: COLORS.LIGHT_SECONDARY,
                      mt: 2,
                      fontFamily: FONT_FAMILY
                    }}
                  >
                    No photos added yet
                  </Typography>
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.BODY_SMALL,
                      color: COLORS.LIGHT_SECONDARY,
                      mt: 1,
                      fontFamily: FONT_FAMILY
                    }}
                  >
                    Click "Add Photo with Annotations" to start adding photos with drawing tools
                  </Typography>
                </Box>
              )}

              {/* Photo Upload Dialog */}
              <PhotoUploadWithDrawing
                open={showPhotoUploadDialog}
                onClose={() => setShowPhotoUploadDialog(false)}
                onSave={handlePhotoUploadWithDrawing}
                maxPhotos={4}
              />
            </Box>

            {/* ================= SUBMIT BUTTON ================= */}
            <Box display="flex" justifyContent="center" mt={4}>
              <Button
                variant="contained"
                size="large"
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Icon name="save" />}
                sx={{
                  px: 4,
                  py: 1.2,
                  borderRadius: "10px",
                  fontFamily: FONT_FAMILY,
                  backgroundColor: COLORS.PRIMARY,
                  textTransform: "none",
                  "&:disabled": {
                    backgroundColor: COLORS.LIGHT_SECONDARY,
                  }
                }}
                onClick={handleUpdateWMR}
                disabled={saving}
              >
                {saving ? "Updating..." : "UPDATE WEEKLY MONITORING REPORT"}
              </Button>
            </Box>
          </>
        )}

        {/* Toast Component */}
        <AppToast
          open={toast.open}
          type={toast.type}
          title={toast.title}
          message={toast.message}
          onClose={closeToast}
        />
      </Box>
    </LocalizationProvider>
  );
}

/* ================= STYLES ================= */

const labelStyle = {
  ...TYPOGRAPHY.H4,
  color: COLORS.PRIMARY,
  mb: 0.5,
  fontFamily: FONT_FAMILY,
};

const inputStyle = {
  "& .MuiOutlinedInput-root": {
    height: "38px",
    borderRadius: "8px",
    fontFamily: FONT_FAMILY,
    "& fieldset": {
      borderColor: COLORS.LIGHT_SECONDARY,
    },
    "&:hover fieldset": {
      borderColor: COLORS.SECONDARY,
    },
    "&.Mui-focused fieldset": {
      borderColor: COLORS.SECONDARY,
    },
  },
  "& input::placeholder": {
    color: COLORS.LIGHT_SECONDARY,
    fontSize: "13px",
  },
};

const addBtnStyle = {
  borderRadius: "7px",
  textTransform: "none",
  backgroundColor: COLORS.SECONDARY,
  color: "#fff",
  fontFamily: FONT_FAMILY,
  px: 2.5,
  "&:hover": {
    backgroundColor: COLORS.PRIMARY,
  }
};

const cellStyle = {
  height: "44px",
  borderRadius: "8px",
  border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
  display: "flex",
  alignItems: "center",
  fontFamily: FONT_FAMILY,
  color: COLORS.SECONDARY,
};

const machineCellStyle = {
  height: "44px",
  borderRadius: "8px",
  border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
  display: "flex",
  alignItems: "center",
  fontFamily: FONT_FAMILY,
  color: COLORS.SECONDARY,
  paddingLeft: "12px",
};
